// src/pages/Infrastructure.js
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAgents, getClients } from "../api";
import { tokens, sx } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import { useAuth } from "../context/AuthContext";
import TimeTracker from "../components/TimeTracker";

const API   = "https://ticketing.midtc.com/api";
const tok   = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, {
    headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });

// ─── Constants ──────────────────────────────────────────────────────────────
const STATUSES = ["Planning", "In Progress", "On Hold", "Completed", "Cancelled"];

const STATUS_CFG = {
  "Planning":     { color: "#3b82f6", dim: "rgba(59,130,246,0.12)",  dot: "#3b82f6" },
  "In Progress":  { color: "#a78bfa", dim: "rgba(167,139,250,0.12)", dot: "#a78bfa" },
  "On Hold":      { color: "var(--badge-amber)", dim: "rgba(251,191,36,0.12)",  dot: "#fbbf24" },
  "Completed":    { color: "#34d399", dim: "rgba(52,211,153,0.12)",  dot: "#34d399" },
  "Cancelled":    { color: "#6b7280", dim: "rgba(107,114,128,0.12)", dot: "#6b7280" },
};

const PRIORITY_CFG = {
  Critical: { color: "#ef4444", bg: "rgba(239,68,68,0.15)"  },
  High:     { color: "#f97316", bg: "rgba(249,115,22,0.15)" },
  Medium:   { color: "var(--badge-amber)", bg: "rgba(251,191,36,0.15)" },
  Low:      { color: "#34d399", bg: "rgba(52,211,153,0.15)" },
};

const NODE_TYPES = {
  server:      { label: "Server",        color: "#3b82f6", symbol: "SRV", shape: "rect"     },
  workstation: { label: "Workstation",   color: "#06b6d4", symbol: "WS",  shape: "rect"     },
  switch:      { label: "Switch",        color: "#10b981", symbol: "SW",  shape: "rect"     },
  router:      { label: "Router",        color: "#f59e0b", symbol: "RT",  shape: "rect"     },
  firewall:    { label: "Firewall",      color: "#ef4444", symbol: "FW",  shape: "rect"     },
  cloud:       { label: "Cloud Service", color: "#8b5cf6", symbol: "CLD", shape: "rounded"  },
  database:    { label: "Database",      color: "#f97316", symbol: "DB",  shape: "rect"     },
  lb:          { label: "Load Balancer", color: "#ec4899", symbol: "LB",  shape: "rect"     },
  note:        { label: "Note",          color: "var(--badge-amber)", symbol: "✎",   shape: "note"     },
  box:         { label: "Box / Label",   color: "#64748b", symbol: "□",   shape: "box"      },
  diamond:     { label: "Decision",      color: "#c084fc", symbol: "◇",   shape: "diamond"  },
  person:      { label: "Person / User", color: "#22d3ee", symbol: "👤",  shape: "rounded"  },
};

const MAP_TYPES = ["network", "server", "cloud", "custom"];
const NODE_W    = 130;
const NODE_H    = 70;
const GRID_SNAP = 20;

const fmt  = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
const fmtL = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const uid  = () => Math.random().toString(36).slice(2, 10);
const ini  = (n = "") => n.split(" ").map(p => p[0]).join("").toUpperCase().slice(0, 2);
const snap = (v, on) => on ? Math.round(v / GRID_SNAP) * GRID_SNAP : v;
// See-through version of a colour. Theme colours are "var(--…)", which can't take a hex alpha
// suffix ("var(--badge-amber)33" is invalid → black or no colour), so mix with transparent instead.
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

function getAnchor(fn, tn) {
  const fw = fn.w || NODE_W, fh = fn.h || NODE_H;
  const tw = tn.w || NODE_W, th = tn.h || NODE_H;
  const fcx = fn.x + fw / 2, fcy = fn.y + fh / 2;
  const tcx = tn.x + tw / 2, tcy = tn.y + th / 2;
  const dx = tcx - fcx, dy = tcy - fcy;
  let x1, y1, x2, y2;
  if (Math.abs(dx) > Math.abs(dy)) {
    x1 = fcx + (dx > 0 ?  fw / 2 : -fw / 2); y1 = fcy;
    x2 = tcx + (dx > 0 ? -tw / 2 :  tw / 2); y2 = tcy;
  } else {
    x1 = fcx; y1 = fcy + (dy > 0 ?  fh / 2 : -fh / 2);
    x2 = tcx; y2 = tcy + (dy > 0 ? -th / 2 :  th / 2);
  }
  return { x1, y1, x2, y2 };
}

function edgePathD(x1, y1, x2, y2, style = "straight") {
  if (style === "curved") {
    const dx = (x2 - x1) * 0.55;
    return `M ${x1},${y1} C ${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
  }
  if (style === "elbow") {
    const mx = (x1 + x2) / 2;
    return `M ${x1},${y1} L ${mx},${y1} L ${mx},${y2} L ${x2},${y2}`;
  }
  return `M ${x1},${y1} L ${x2},${y2}`;
}

// ─── Atom components ─────────────────────────────────────────────────────────
const Avatar = ({ name, size = 26 }) => (
  <div title={name} style={{
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    background: "linear-gradient(135deg,#4f6fff,#7b61ff)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: size * 0.38, fontWeight: 700, color: "#fff",
  }}>{ini(name)}</div>
);

const Chip = ({ label, color, bg }) => (
  <span style={{
    background: bg || tint(color, 13), color, border: `1px solid ${tint(color, 27)}`,
    borderRadius: 99, padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
  }}>{label}</span>
);

// ─── Main Component ──────────────────────────────────────────────────────────
export default function Infrastructure() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tab, setTab]   = useState("projects");
  const [agents, setAgents]   = useState([]);
  const [clients, setClients] = useState([]);

  const [projects, setProjects]     = useState([]);
  const [projLoading, setProjLoad]  = useState(true);
  const [view, setView]             = useState("board");
  const [selected, setSelected]     = useState(null);
  const [updating, setUpdating]     = useState(false);
  const [filterStatus, setFS]       = useState("");
  const [filterClient, setFC]       = useState("");
  const [filterSearch, setFSearch]  = useState("");
  const [filterMine, setFilterMine] = useState(false);
  const [showNew, setShowNew]       = useState(false);
  const { agent: me } = useAuth();

  const [maps, setMaps]             = useState([]);
  const [mapsLoad, setMapsLoad]     = useState(false);
  const [activeMap, setActiveMap]   = useState(null);
  const [showNewMap, setShowNewMap] = useState(false);

  // A save that fails must say so — the page used to show the change, then quietly
  // snap back to the old value on the next reload (the "assignee disappears" report).
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  const notify = useCallback((text) => {
    clearTimeout(noticeTimer.current);
    setNotice(text);
    noticeTimer.current = setTimeout(() => setNotice(null), 7000);
  }, []);

  // Saves go out one at a time, in order, and the list is reloaded once the last one is done.
  // Before, each save reloaded the list as soon as it finished: when the next edit was already
  // on its way, that reload put the values from BEFORE it back on screen, and the edit after
  // that saved on top of them — the earlier change was lost (an assigned task "lost" its name,
  // milestones vanished; seen in the E2E run 10-23).
  const saveQueue    = useRef(Promise.resolve());
  const pendingSaves = useRef(0);
  const loadSeq      = useRef(0);
  const savedSince   = useRef({});   // project id → fields saved since the last reload

  // `quiet` refreshes the data without swapping the board for "Loading…" — every save
  // reloads, and the whole board used to blink on each one.
  const loadProjects = useCallback(async ({ quiet = false } = {}) => {
    const seq = ++loadSeq.current;
    if (!quiet) setProjLoad(true);
    try {
      const p = new URLSearchParams();
      if (filterStatus) p.append("status", filterStatus);
      if (filterClient) p.append("client_id", filterClient);
      const r = await apiFetch(`/infrastructure-projects?${p}`);
      const d = await r.json();
      // Only the newest load lands, and never while a save is still going out (it would be stale).
      if (seq === loadSeq.current && pendingSaves.current === 0) setProjects(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
    finally { if (!quiet) setProjLoad(false); }
  }, [filterStatus, filterClient]);
  const loadRef = useRef(loadProjects);
  loadRef.current = loadProjects;

  const queueSave = (job) => {
    pendingSaves.current++;
    setUpdating(true);
    const run = async () => {
      try { await job(); }
      catch { notify("Couldn't reach the server — that change wasn't saved."); }
      finally {
        if (--pendingSaves.current === 0) {
          await loadRef.current({ quiet: true });   // show what the server really has
          if (pendingSaves.current === 0) {         // …unless another edit was queued meanwhile — its turn will do it
            const saved = savedSince.current; savedSince.current = {};
            setSelected(s => (s && saved[s.id] ? { ...s, ...saved[s.id] } : s));
            setUpdating(false);
          }
        }
      }
    };
    saveQueue.current = saveQueue.current.then(run);
    return saveQueue.current;
  };

  const loadMaps = useCallback(async () => {
    setMapsLoad(true);
    try {
      const r = await apiFetch("/infrastructure-maps");
      const d = await r.json();
      setMaps(Array.isArray(d) ? d : []);
    } finally { setMapsLoad(false); }
  }, []);

  useEffect(() => { if (tab === "projects") loadProjects(); }, [loadProjects, tab]);
  useEffect(() => { if (tab === "maps") loadMaps(); }, [loadMaps, tab]);
  useEffect(() => {
    getAgents().then(r => setAgents(r.data || [])).catch(() => {});
    getClients().then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  // Deep link: /projects?project=<id> opens that project's panel (used by the
  // team emails and Teams cards so "Open Project" lands on the right project).
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current || !projects.length) return;
    const wanted = new URLSearchParams(window.location.search).get("project");
    if (!wanted) { deepLinkedRef.current = true; return; }
    const p = projects.find(x => x.id === wanted);
    if (p) { setSelected(p); deepLinkedRef.current = true; }
  }, [projects]);

  // Keep the open panel in sync with fresh list data (comments/mirrors update
  // assigned_details, ticket link, etc).
  useEffect(() => {
    if (!selected || pendingSaves.current > 0) return;   // mid-save the panel already shows the newest values
    const fresh = projects.find(x => x.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const handleUpdate = (id, updates) => {
    // Show the change in the panel right away; the reload after the last save confirms it
    // (or, if the save failed, puts the server's value back).
    setSelected(s => (s?.id === id ? { ...s, ...updates } : s));
    return queueSave(async () => {
      const r = await apiFetch(`/infrastructure-projects/${id}`, { method: "PATCH", body: JSON.stringify(updates) });
      if (r.ok) savedSince.current[id] = { ...savedSince.current[id], ...updates };
      else { const d = await r.json().catch(() => ({})); notify(d.error ? `Couldn't save: ${d.error}` : "Couldn't save that change — please try again."); }
    });
  };

  // Board drag: apply the new order locally first so the card moves under the
  // cursor immediately, then persist (in the save queue). The reload after it shows the truth.
  const handleReorder = (status, orderedIds, movedId) => {
    setProjects(prev => {
      const pos = new Map(orderedIds.map((id, i) => [id, i]));
      const patched = prev.map(p => {
        const next = { ...p };
        if (p.id === movedId) next.status = status;
        if (pos.has(p.id)) next.sort_order = pos.get(p.id);
        return next;
      });
      // Re-sort locally with the SAME comparator the API uses. Without this the
      // array order never changes, so the card would stay put on screen even
      // though the move persisted — which is exactly what it looked like.
      const sRank = { "In Progress": 1, "Planning": 2, "On Hold": 3 };
      const pRank = { Critical: 1, High: 2, Medium: 3, Low: 4 };
      return patched.sort((a, b) =>
        (sRank[a.status] || 4) - (sRank[b.status] || 4) ||
        (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity) ||
        (pRank[a.priority] || 4) - (pRank[b.priority] || 4) ||
        new Date(b.created_at) - new Date(a.created_at)
      );
    });
    return queueSave(async () => {
      const r = await apiFetch("/infrastructure-projects/reorder", {
        method: "POST",
        body: JSON.stringify({ status, ordered_ids: orderedIds, moved_id: movedId }),
      }).catch(() => null);
      if (r?.ok) savedSince.current[movedId] = { ...savedSince.current[movedId], status };
      else notify("Couldn't move that project — please try again.");
    });
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this project?")) return;
    return queueSave(async () => {
      const r = await apiFetch(`/infrastructure-projects/${id}`, { method: "DELETE" }).catch(() => null);
      if (!r?.ok) {
        notify(r?.status === 403 ? "Only admins, managers and developers can delete projects." : "Couldn't delete the project — please try again.");
        return;
      }
      setSelected(null);
    });
  };

  const searchedProjects = useMemo(() =>
    projects.filter(p =>
      (!filterSearch || p.title.toLowerCase().includes(filterSearch.toLowerCase())) &&
      (!filterMine || (p.assigned_details || []).some(d => d.id === me?.id) || p.created_by === me?.id)
    ),
  [projects, filterSearch, filterMine, me]);

  const grouped = useMemo(() =>
    STATUSES.reduce((acc, s) => {
      acc[s] = searchedProjects.filter(p =>
        p.status === s &&
        (!filterClient || p.client_id === filterClient)
      );
      return acc;
    }, {}),
  [searchedProjects, filterClient]);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", background: tokens.surface.page }}>
      <div style={{ padding: isMobile ? "14px 14px 0" : "20px 24px 0", borderBottom: `1px solid ${tokens.border.soft}`, flexShrink: 0,
        background: tokens.gradient.listHead }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 14, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 10 : 0 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: tokens.text.primary, letterSpacing: "-0.5px" }}>Projects &amp; Maps</h1>
            <p style={{ margin: "3px 0 0", color: tokens.text.faint, fontSize: 13 }}>
              {tab === "projects"
                ? `${projects.length} project${projects.length !== 1 ? "s" : ""}`
                : `${maps.length} diagram${maps.length !== 1 ? "s" : ""}`}
            </p>
          </div>
          {tab === "projects" && (
            <button onClick={() => setShowNew(true)} style={sx.buttonPrimary}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Project
            </button>
          )}
          {tab === "maps" && (
            <button onClick={() => setShowNewMap(true)} style={sx.buttonPrimary}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Diagram
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          {[{ k: "projects", l: "Projects" }, { k: "maps", l: "Maps" }].map(t => {
            const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} style={{
                padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: "none", border: "none", fontFamily: "inherit",
                borderBottom: `2px solid ${active ? tokens.accent.indigo2 : "transparent"}`,
                color: active ? tokens.accent.indigo2 : tokens.text.faint,
                transition: "all 0.15s",
              }}>{t.l}</button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflow: isMobile ? "auto" : "hidden", display: "flex" }}>
        {tab === "projects" && (
          <ProjectsView
            projects={searchedProjects} loading={projLoading} view={view} setView={setView}
            selected={selected} setSelected={setSelected} updating={updating}
            filterStatus={filterStatus} setFilterStatus={setFS}
            filterClient={filterClient} setFilterClient={setFC}
            filterSearch={filterSearch} setFilterSearch={setFSearch}
            filterMine={filterMine} setFilterMine={setFilterMine}
            grouped={grouped} agents={agents} clients={clients}
            onUpdate={handleUpdate} onDelete={handleDelete} onReorder={handleReorder}
            onReload={loadProjects} navigate={navigate}
          />
        )}
        {tab === "maps" && (
          <MapsView
            maps={maps} loading={mapsLoad} clients={clients} agents={agents}
            activeMap={activeMap} setActiveMap={setActiveMap}
            showNewMap={showNewMap} setShowNewMap={setShowNewMap}
            onReload={loadMaps} notify={notify}
          />
        )}
      </div>

      {showNew && (
        <NewProjectModal
          agents={agents} clients={clients}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); loadProjects({ quiet: true }); }}
        />
      )}

      {notice && (
        <div role="alert" style={{ position: "fixed", right: 20, bottom: 20, zIndex: 2000, maxWidth: 420, display: "flex", gap: 10, alignItems: "flex-start",
          background: "rgba(127,29,29,0.95)", border: "1px solid rgba(248,113,113,0.6)", color: "#fee2e2", borderRadius: 10, padding: "11px 14px",
          fontSize: 13, lineHeight: 1.5, boxShadow: "0 12px 30px rgba(0,0,0,0.45)" }}>
          <span style={{ flex: 1 }}>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss" style={{ background: "none", border: "none", color: "#fecaca", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}
    </div>
  );
}

// ─── Projects View ───────────────────────────────────────────────────────────
function ProjectsView({
  projects, loading, view, setView, selected, setSelected, updating,
  filterStatus, setFilterStatus, filterClient, setFilterClient,
  filterSearch, setFilterSearch, filterMine, setFilterMine,
  grouped, agents, clients, onUpdate, onDelete, onReorder, onReload, navigate,
}) {
  const isMobile = useIsMobile();
  const [dragId, setDragId]               = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);   // {status,index} insertion point
  const [panelMode, setPanelMode] = useState("rail");   // rail | full
  const dragCounters = useRef({});

  // Drop at an explicit position. `index` is where the card should land in the
  // target column (null = append). This handles BOTH re-ordering inside a column
  // and moving between columns — previously a same-column drop did nothing at all.
  const handleDrop = (status, e, index = null) => {
    const id = e?.dataTransfer?.getData("text/plain") || dragId;
    dragCounters.current = {};
    setDragId(null); setDragOverStatus(null); setDropTarget(null);
    if (!id) return;
    const proj = projects.find(p => p.id === id);
    if (!proj) return;

    const column = (grouped[status] || []).filter(p => p.id !== id);
    const at = index == null ? column.length : Math.max(0, Math.min(index, column.length));
    const orderedIds = [...column.slice(0, at).map(p => p.id), id, ...column.slice(at).map(p => p.id)];

    const samePlace = proj.status === status &&
      JSON.stringify(orderedIds) === JSON.stringify((grouped[status] || []).map(p => p.id));
    if (samePlace) return;

    onReorder(status, orderedIds, id);
  };

  const colDragProps = (status) => ({
    onDragEnter: (e) => {
      e.preventDefault();
      dragCounters.current[status] = (dragCounters.current[status] || 0) + 1;
      setDragOverStatus(status);
    },
    onDragLeave: () => {
      dragCounters.current[status] = Math.max(0, (dragCounters.current[status] || 0) - 1);
      if (dragCounters.current[status] === 0) setDragOverStatus(s => s === status ? null : s);
    },
    onDragOver:  (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
    onDrop:      (e) => { e.preventDefault(); handleDrop(status, e, null); },
  });

  // Drop zone between/over cards — gives the card an insertion point.
  const cardDropProps = (status, index) => ({
    onDragOver: (e) => {
      e.preventDefault(); e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      // Above the midpoint inserts before this card, below inserts after.
      const r = e.currentTarget.getBoundingClientRect();
      const half = e.clientY < r.top + r.height / 2 ? 0 : 1;
      setDropTarget({ status, index: index + half });
    },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      const r = e.currentTarget.getBoundingClientRect();
      const half = e.clientY < r.top + r.height / 2 ? 0 : 1;
      handleDrop(status, e, index + half);
    },
  });

  // ── Derived stats for the summary bar ──
  const stats = useMemo(() => {
    const now = Date.now();
    const total       = projects.length;
    const planning    = projects.filter(p => p.status === "Planning").length;
    const inProgress  = projects.filter(p => p.status === "In Progress").length;
    const onHold      = projects.filter(p => p.status === "On Hold").length;
    const completed   = projects.filter(p => p.status === "Completed").length;
    const atRisk      = projects.filter(p => {
      if (p.status === "Completed" || p.status === "Cancelled") return false;
      if (p.due_date && new Date(p.due_date).getTime() < now) return true;
      return p.priority === "Critical";
    }).length;
    return { total, planning, inProgress, onHold, completed, atRisk };
  }, [projects]);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "visible" : "hidden" }}>
      <div style={{ flex: isMobile ? "none" : 1, display: "flex", flexDirection: "column", overflow: isMobile ? "visible" : "hidden" }}>

        {/* Stats summary row */}
        <div style={{
          padding: "14px 16px", borderBottom: `1px solid ${tokens.border.soft}`,
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10, flexShrink: 0, background: "rgba(13,21,38,0.5)",
        }}>
          {[
            { label: "Total Projects", value: stats.total,      color: tokens.text.primary },
            { label: "In Progress",    value: stats.inProgress, color: STATUS_CFG["In Progress"].color },
            { label: "Planning",       value: stats.planning,   color: STATUS_CFG["Planning"].color },
            { label: "On Hold",        value: stats.onHold,     color: STATUS_CFG["On Hold"].color },
            { label: "At Risk",        value: stats.atRisk,     color: tokens.accent.red,    glow: stats.atRisk > 0 },
            { label: "Completed",      value: stats.completed,  color: STATUS_CFG["Completed"].color },
          ].map(s => (
            <div key={s.label} style={{
              background: tokens.surface.panel,
              border: `1px solid ${s.glow ? "rgba(239,68,68,0.35)" : tokens.border.soft}`,
              borderRadius: tokens.radius.md, padding: "11px 13px",
              boxShadow: s.glow ? "0 0 12px rgba(239,68,68,0.18)" : tokens.shadow.panel,
              transition: "all 0.18s",
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 9, color: tokens.text.label, marginTop: 5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${tokens.border.soft}`, display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <div style={{ display: "flex", background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`, borderRadius: 7, overflow: "hidden" }}>
            {[{ k: "board", l: "⊞ Board" }, { k: "list", l: "☰ List" }, { k: "timeline", l: "⟷ Timeline" }].map(v => {
              const active = view === v.k;
              return (
                <button key={v.k} onClick={() => setView(v.k)} style={{
                  padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  border: "none",
                  background: active ? tokens.gradient.primary : "transparent",
                  color: active ? "#fff" : tokens.text.faint,
                  boxShadow: active ? "inset 0 0 8px rgba(99,102,241,0.5)" : "none",
                }}>{v.l}</button>
              );
            })}
          </div>
          <div style={{ width: 1, height: 20, background: "var(--t-surface-mid)" }} />
          <button onClick={() => setFilterMine(m => !m)}
            style={{
              background: filterMine ? "rgba(79,111,255,0.18)" : "var(--t-panel)",
              border: `1px solid ${filterMine ? "#4f6fff" : "var(--t-border-soft)"}`,
              color: filterMine ? "#93c5fd" : "#94a3b8",
              borderRadius: 99, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>
            {filterMine ? "✓ My Projects" : "My Projects"}
          </button>
          <input value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
            placeholder="Search projects…"
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "5px 10px", color: "#cbd5e1", fontSize: 12, width: isMobile ? undefined : 160, flex: isMobile ? "1 1 100%" : undefined }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "5px 10px", color: "#cbd5e1", fontSize: 12, cursor: "pointer" }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "5px 10px", color: "#cbd5e1", fontSize: 12, cursor: "pointer" }}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          <button onClick={onReload} title="Refresh"
            style={{ marginLeft: "auto", background: "none", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "5px 10px", color: "#64748b", cursor: "pointer", fontSize: 14 }}>
            ↺
          </button>
        </div>

        <div style={{ flex: isMobile ? "none" : 1, overflow: "auto", padding: isMobile ? "14px" : "20px" }}>
          {loading && <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>Loading projects…</div>}

          {!loading && view === "board" && (
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: "max-content" }}>
              {STATUSES.map(status => {
                const cfg    = STATUS_CFG[status];
                const col    = grouped[status] || [];
                const isOver = dragOverStatus === status;
                return (
                  <div key={status} style={{ width: 270, flexShrink: 0 }}
                    {...colDragProps(status)}>
                    {/* Column header */}
                    <div style={{
                      background: isOver ? tint(cfg.color, 13) : "var(--t-panel)",
                      border: `1px solid ${isOver ? cfg.color : "var(--t-border-soft)"}`,
                      borderRadius: "10px 10px 0 0", borderTop: `3px solid ${cfg.color}`,
                      padding: "10px 14px", display: "flex", alignItems: "center", gap: 8,
                      transition: "background 0.15s, border-color 0.15s",
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: "0.06em", flex: 1 }}>{status}</span>
                      <span style={{ background: cfg.dim, color: cfg.color, borderRadius: 99, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{col.length}</span>
                    </div>
                    {/* Cards area */}
                    <div style={{
                      background: isOver ? tint(cfg.color, 4) : "var(--t-panel-hi)",
                      border: `1px solid ${isOver ? tint(cfg.color, 53) : "var(--t-border-soft)"}`,
                      borderTop: "none", borderRadius: "0 0 10px 10px",
                      minHeight: 100, padding: 8,
                      transition: "background 0.15s, border-color 0.15s",
                      boxShadow: isOver ? `inset 0 0 0 2px ${tint(cfg.color, 27)}` : "none",
                    }}>
                      {col.length === 0 && (
                        <div style={{ textAlign: "center", padding: "24px 8px", color: isOver ? cfg.color : "var(--t-border-strong)", fontSize: 12, fontWeight: isOver ? 600 : 400, transition: "color 0.15s" }}>
                          {isOver ? `Drop to move → ${status}` : "No projects"}
                        </div>
                      )}
                      {col.map((p, i) => (
                        <div key={p.id} {...cardDropProps(status, i)}>
                          {dropTarget?.status === status && dropTarget.index === i && (
                            <div style={{ height: 3, background: "#4f6fff", borderRadius: 2, margin: "2px 0 5px", boxShadow: "0 0 8px rgba(79,111,255,0.8)" }} />
                          )}
                          <ProjectCard project={p} isSelected={selected?.id === p.id}
                            onClick={() => setSelected(s => s?.id === p.id ? null : p)}
                            onDragStart={() => setDragId(p.id)}
                            onDragEnd={() => { setDragId(null); setDragOverStatus(null); setDropTarget(null); dragCounters.current = {}; }}
                            isDragging={dragId === p.id} />
                        </div>
                      ))}
                      {dropTarget?.status === status && dropTarget.index >= col.length && col.length > 0 && (
                        <div style={{ height: 3, background: "#4f6fff", borderRadius: 2, margin: "2px 0", boxShadow: "0 0 8px rgba(79,111,255,0.8)" }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && view === "list" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 100px 110px 120px 80px", gap: 8, padding: "0 14px 8px", borderBottom: "1px solid var(--t-border-soft)" }}>
                {["Project", "Status", "Priority", "Due Date", "Assigned To", "Progress"].map(h => (
                  <span key={h} style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</span>
                ))}
              </div>
              {projects.filter(p => !filterClient || p.client_id === filterClient).map(p => {
                const pc = PRIORITY_CFG[p.priority] || {};
                const sc = STATUS_CFG[p.status] || {};
                const overdue = p.due_date && new Date(p.due_date) < new Date();
                const tasks = Array.isArray(p.tasks) ? p.tasks : [];
                const done  = tasks.filter(t => t.done).length;
                const pct   = tasks.length ? Math.round(done / tasks.length * 100) : null;
                return (
                  <div key={p.id}
                    onClick={() => setSelected(s => s?.id === p.id ? null : p)}
                    style={{
                      display: "grid", gridTemplateColumns: "1fr 130px 100px 110px 120px 80px", gap: 8,
                      padding: "11px 14px", cursor: "pointer", borderBottom: "1px solid var(--t-border-soft)",
                      background: selected?.id === p.id ? "var(--t-surface-mid)" : "transparent",
                      transition: "background 0.12s",
                      alignItems: "center",
                    }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t-text-body)" }}>{p.title}</div>
                      <div style={{ fontSize: 11, color: "#475569", marginTop: 2, display: "flex", gap: 8 }}>
                        {p.client_name && <span>{p.client_name}</span>}
                        {p.source_ticket_number && <span style={{ color: "#60a5fa" }}>🎫 TKT-{String(p.source_ticket_number).padStart(4, "0")}</span>}
                      </div>
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <select value={p.status} onChange={e => onUpdate(p.id, { status: e.target.value })}
                        style={{ background: sc.dim || "var(--t-panel)", color: sc.color || "#94a3b8", border: `1px solid ${tint(sc.color || "#64748b", 27)}`,
                          borderRadius: 99, padding: "3px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><Chip label={p.priority} color={pc.color || "#64748b"} bg={pc.bg} /></div>
                    <div style={{ fontSize: 12, color: overdue ? "#f87171" : "#64748b" }}>{fmt(p.due_date) || "—"}</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {(p.assigned_details || []).slice(0, 3).map(d => <Avatar key={d.id} name={d.name} size={22} />)}
                    </div>
                    <div>
                      {pct !== null ? (
                        <div>
                          <div style={{ height: 4, background: "var(--t-surface-mid)", borderRadius: 2, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#34d399" : "#3b82f6", borderRadius: 2 }} />
                          </div>
                          <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{pct}%</div>
                        </div>
                      ) : <span style={{ fontSize: 11, color: "var(--t-border-strong)" }}>—</span>}
                    </div>
                  </div>
                );
              })}
              {projects.length === 0 && <div style={{ textAlign: "center", padding: 48, color: "#475569" }}><div style={{ fontSize: 32, marginBottom: 8 }}>🏗️</div><div>No projects yet</div></div>}
            </div>
          )}

          {!loading && view === "timeline" && (
            <TimelineView projects={projects.filter(p => !filterClient || p.client_id === filterClient)} onSelect={setSelected} selected={selected} />
          )}
        </div>
      </div>

      {selected && panelMode === "rail" && (
        <ProjectDetailPanel
          project={selected} agents={agents} updating={updating}
          onUpdate={onUpdate} onDelete={onDelete} onClose={() => setSelected(null)}
          navigate={navigate} mode="rail" onToggleSize={() => setPanelMode("full")}
        />
      )}

      {/* Enlarged: an overlay ON TOP of the board, which stays mounted and live
          underneath so minimising returns to exactly the same place. */}
      {selected && panelMode === "full" && (
        <div
          onMouseDown={e => { if (e.target === e.currentTarget) setPanelMode("rail"); }}
          style={{
            position: "fixed", inset: 0, zIndex: 1400,
            background: "rgba(2,6,23,0.72)", backdropFilter: "blur(3px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: isMobile ? 0 : "3vh 3vw",
          }}>
          <div style={{
            width: "100%", height: "100%", maxWidth: 1200,
            background: "var(--t-page)", border: "1px solid var(--t-border-soft)",
            borderRadius: isMobile ? 0 : 14, overflow: "hidden",
            boxShadow: "0 30px 90px rgba(0,0,0,0.6)", display: "flex",
          }}>
            <ProjectDetailPanel
              project={selected} agents={agents} updating={updating}
              onUpdate={onUpdate} onDelete={onDelete} onClose={() => { setPanelMode("rail"); setSelected(null); }}
              navigate={navigate} mode="full" onToggleSize={() => setPanelMode("rail")}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Project Card ────────────────────────────────────────────────────────────
function ProjectCard({ project: p, isSelected, onClick, onDragStart, onDragEnd, isDragging }) {
  const pc      = PRIORITY_CFG[p.priority] || {};
  const overdue = p.due_date && new Date(p.due_date) < new Date() && !["Completed","Cancelled"].includes(p.status);
  const tasks   = Array.isArray(p.tasks) ? p.tasks : [];
  const done    = tasks.filter(t => t.done).length;
  const pct     = tasks.length ? Math.round(done / tasks.length * 100) : null;
  return (
    <div
      draggable
      onDragStart={e => {
        // setData is REQUIRED for HTML5 drag to start in Firefox — without it the
        // whole board's "move and drop" silently did nothing there.
        e.dataTransfer.setData("text/plain", p.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.();
      }}
      onDragEnd={onDragEnd}
      onClick={onClick}
      style={{
        background: isSelected ? "var(--t-surface-mid)" : "var(--t-panel)",
        border: `1px solid ${isSelected ? "#2563eb" : "var(--t-border-soft)"}`,
        borderLeft: `3px solid ${pc.color || "var(--t-border-strong)"}`,
        borderRadius: 10, padding: "11px 12px", cursor: isDragging ? "grabbing" : "grab", marginBottom: 7,
        opacity: isDragging ? 0.45 : 1,
        transform: isDragging ? "rotate(1.5deg) scale(0.97)" : "none",
        transition: isDragging ? "none" : "all 0.12s",
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.5)" : "0 1px 2px rgba(0,0,0,0.2)",
      }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t-text-body)", marginBottom: 7, lineHeight: 1.4 }}>{p.title}</div>
      {p.client_name && (
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
          {p.client_name}
        </div>
      )}
      {pct !== null && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ height: 3, background: "var(--t-surface-mid)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#34d399" : "#3b82f6", borderRadius: 2, transition: "width 0.3s" }} />
          </div>
          <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{done}/{tasks.length} tasks · {pct}%</div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <div style={{ display: "flex", gap: 3 }}>
          {(p.assigned_details || []).slice(0, 4).map(d => <Avatar key={d.id} name={d.name} size={22} />)}
          {(p.assigned_details || []).length === 0 && <span style={{ fontSize: 11, color: "var(--t-border-strong)" }}>Unassigned</span>}
        </div>
        {p.due_date && <span style={{ fontSize: 11, color: overdue ? "#f87171" : "#475569" }}>{overdue && "⚠ "}{fmt(p.due_date)}</span>}
      </div>
      <div style={{ marginTop: 8, display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
        <Chip label={p.priority} color={pc.color || "#64748b"} bg={pc.bg} />
        {p.source_ticket_number && (
          <span title={p.source_subject} style={{ fontSize: 10, color: "#60a5fa", background: "rgba(37,99,235,0.1)", border: "1px solid rgba(37,99,235,0.25)", borderRadius: 99, padding: "2px 7px", fontWeight: 700 }}>
            🎫 TKT-{String(p.source_ticket_number).padStart(4, "0")}
          </span>
        )}
        {p.goal && <span title={p.goal} style={{ fontSize: 10, color: "#f59e0b" }}>🎯</span>}
      </div>
    </div>
  );
}

// ─── Timeline / Gantt View ───────────────────────────────────────────────────
function TimelineView({ projects, onSelect, selected }) {
  const today = new Date();
  const projs = projects.filter(p => p.due_date || p.start_date || p.created_at);

  const allDates = projs.flatMap(p => [
    p.start_date ? new Date(p.start_date) : new Date(p.created_at),
    p.due_date   ? new Date(p.due_date)   : null,
  ].filter(Boolean));
  const minDate = allDates.length ? new Date(Math.min(...allDates)) : new Date(today.getFullYear(), today.getMonth(), 1);
  const maxDate = allDates.length ? new Date(Math.max(...allDates)) : new Date(today.getFullYear(), today.getMonth() + 2, 0);
  minDate.setDate(minDate.getDate() - 7);
  maxDate.setDate(maxDate.getDate() + 14);

  const totalDays = Math.ceil((maxDate - minDate) / 86400000);
  const DAY_W = Math.max(24, Math.min(48, 1100 / totalDays));
  const ROW_H = 42;
  const LEFT_W = 200;
  const totalW = LEFT_W + totalDays * DAY_W;
  const svgH   = projs.length * ROW_H + 40;

  const toX = (d) => LEFT_W + Math.round((new Date(d) - minDate) / 86400000) * DAY_W;
  const todayX = toX(today);

  const months = [];
  let cur = new Date(minDate);
  while (cur <= maxDate) {
    months.push({ label: cur.toLocaleDateString("en-US", { month: "short", year: "numeric" }), x: toX(cur) });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }

  if (projs.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
        <div>No projects with dates to show in timeline</div>
      </div>
    );
  }

  return (
    <div style={{ overflow: "auto", width: "100%" }}>
      <svg width={totalW} height={svgH} style={{ display: "block", fontFamily: "system-ui" }}>
        {/* Header: months */}
        <rect x={0} y={0} width={totalW} height={32} fill="var(--t-panel-hi)" />
        <rect x={0} y={0} width={LEFT_W} height={32} fill="var(--t-panel-hi)" />
        {months.map((m, i) => (
          <g key={i}>
            <line x1={m.x} y1={0} x2={m.x} y2={svgH} stroke="var(--t-border-soft)" strokeWidth={1} />
            <text x={m.x + 6} y={20} fill="#64748b" fontSize={11} fontWeight="600">{m.label}</text>
          </g>
        ))}
        {/* Today line */}
        <line x1={todayX} y1={0} x2={todayX} y2={svgH} stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="4,3" />
        <rect x={todayX - 16} y={2} width={32} height={18} rx={4} fill="#4f6fff" />
        <text x={todayX} y={14} fill="#fff" fontSize={9} fontWeight="700" textAnchor="middle">TODAY</text>

        {/* Project rows */}
        {projs.map((p, i) => {
          const y = 32 + i * ROW_H;
          const sc = STATUS_CFG[p.status] || {};
          const pc = PRIORITY_CFG[p.priority] || {};
          const start = p.start_date ? new Date(p.start_date) : new Date(p.created_at);
          const end   = p.due_date   ? new Date(p.due_date)   : new Date(start.getTime() + 7 * 86400000);
          const bx    = toX(start);
          const bw    = Math.max(8, toX(end) - bx);
          const isSel = selected?.id === p.id;
          const tasks = Array.isArray(p.tasks) ? p.tasks : [];
          const pct   = tasks.length ? tasks.filter(t => t.done).length / tasks.length : 0;
          return (
            <g key={p.id} onClick={() => onSelect(s => s?.id === p.id ? null : p)} style={{ cursor: "pointer" }}>
              <rect x={0} y={y} width={totalW} height={ROW_H - 1}
                fill={isSel ? "rgba(37,99,235,0.1)" : i % 2 === 0 ? "var(--t-page)" : "var(--t-panel-hi)"} />
              <text x={LEFT_W - 10} y={y + ROW_H / 2 + 4} fill={isSel ? "#93c5fd" : "#64748b"}
                fontSize={12} fontWeight="600" textAnchor="end">{p.title.slice(0, 22)}{p.title.length > 22 ? "…" : ""}</text>
              {p.client_name && (
                <text x={LEFT_W - 10} y={y + ROW_H / 2 + 15} fill="#475569" fontSize={10} textAnchor="end">{p.client_name}</text>
              )}
              {/* Bar background */}
              <rect x={bx} y={y + 10} width={bw} height={ROW_H - 22} rx={4}
                fill={sc.color ? tint(sc.color, 20) : "var(--t-border-soft)"}
                stroke={isSel ? "#60a5fa" : sc.color || "var(--t-border-strong)"}
                strokeWidth={isSel ? 2 : 1} />
              {/* Progress fill */}
              {pct > 0 && (
                <rect x={bx} y={y + 10} width={Math.round(bw * pct)} height={ROW_H - 22} rx={4} fill={sc.color ? tint(sc.color, 53) : "#3b82f6"} />
              )}
              <text x={bx + 6} y={y + ROW_H / 2 + 5} fill="#f1f5f9" fontSize={10} fontWeight="600">
                {p.status}
              </text>
            </g>
          );
        })}
        {/* Left border */}
        <line x1={LEFT_W} y1={0} x2={LEFT_W} y2={svgH} stroke="var(--t-border-soft)" strokeWidth={1} />
        <rect x={0} y={0} width={LEFT_W} height={32} fill="var(--t-panel-hi)" />
        <text x={LEFT_W - 10} y={20} fill="#475569" fontSize={11} fontWeight="700" textAnchor="end">PROJECT</text>
      </svg>
    </div>
  );
}

// ─── Detail Panel ────────────────────────────────────────────────────────────
function ProjectDetailPanel({ project: p, agents, updating, onUpdate, onDelete, onClose, navigate, mode = "rail", onToggleSize }) {
  const isMobile = useIsMobile();
  const { agent: me } = useAuth();
  const isAdmin = (me?.roles?.length ? me.roles : [me?.role]).includes("admin");   // /projects/:id is admin-only
  const [tab, setTab]           = useState("overview");
  const [notes, setNotes]       = useState(p.notes || "");
  const [tasks, setTasks]       = useState(Array.isArray(p.tasks) ? p.tasks : []);
  const [milestones, setMS]     = useState(Array.isArray(p.milestones) ? p.milestones : []);
  const [goal, setGoal]         = useState(p.goal || "");
  const [newTask, setNewTask]   = useState("");
  const [newMS, setNewMS]       = useState("");
  const [comments, setComments] = useState([]);
  const [commLoad, setCommLoad] = useState(false);
  const [msg, setMsg]           = useState("");
  const [sending, setSending]   = useState(false);
  const [dragTaskId, setDragTaskId]       = useState(null);
  const [dragOverTaskId, setDragOverTaskId] = useState(null);
  const [newTaskAssignee, setNewTaskAssignee] = useState("");
  const [newTaskDue, setNewTaskDue]           = useState("");
  const [addingMember, setAddingMember]       = useState(false);
  const feedRef = useRef(null);

  const team    = p.assigned_details || [];
  const teamIds = (p.assigned_to || []).map(String);

  const addMember = (agentId) => {
    if (!agentId || teamIds.includes(agentId)) return;
    onUpdate(p.id, { assigned_to: [...teamIds, agentId] });
    setAddingMember(false);
  };
  const removeMember = (agentId) => {
    onUpdate(p.id, { assigned_to: teamIds.filter(id => id !== agentId) });
  };

  const agentName = (id) => {
    const a = agents.find(x => x.id === id);
    return a ? `${a.first_name} ${a.last_name}` : null;
  };

  useEffect(() => {
    setNotes(p.notes || "");
    setTasks(Array.isArray(p.tasks) ? p.tasks : []);
    setMS(Array.isArray(p.milestones) ? p.milestones : []);
    setGoal(p.goal || "");
  }, [p.id, p.notes, p.tasks, p.milestones, p.goal]);

  useEffect(() => {
    if (tab !== "activity") return;
    setCommLoad(true);
    apiFetch(`/infrastructure-projects/${p.id}/comments`)
      .then(r => r.json()).then(d => { setComments(Array.isArray(d) ? d : []); })
      .catch(() => {}).finally(() => setCommLoad(false));
  }, [tab, p.id]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [comments]);

  const ipt = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 10px", color: "#f1f5f9", fontSize: 13, width: "100%", boxSizing: "border-box" };
  const pcfg = STATUS_CFG[p.status] || {};

  const saveTasks = (next) => { setTasks(next); onUpdate(p.id, { tasks: next }); };
  const addTask   = () => {
    if (!newTask.trim()) return;
    saveTasks([...tasks, {
      id: uid(), title: newTask.trim(), done: false,
      assignee_id: newTaskAssignee || null,
      assignee_name: newTaskAssignee ? agentName(newTaskAssignee) : null,
      due: newTaskDue || null,
    }]);
    setNewTask(""); setNewTaskAssignee(""); setNewTaskDue("");
  };
  const toggleTask = (id) => saveTasks(tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));
  const deleteTask = (id) => saveTasks(tasks.filter(t => t.id !== id));
  const setTaskAssignee = (id, agentId) => saveTasks(tasks.map(t =>
    t.id === id ? { ...t, assignee_id: agentId || null, assignee_name: agentId ? agentName(agentId) : null } : t));
  const setTaskDue = (id, due) => saveTasks(tasks.map(t => t.id === id ? { ...t, due: due || null } : t));

  const handleTaskDrop = (toId) => {
    if (!dragTaskId || dragTaskId === toId) return;
    const from = tasks.findIndex(t => t.id === dragTaskId);
    const to   = tasks.findIndex(t => t.id === toId);
    if (from < 0 || to < 0) return;
    const next = [...tasks]; const [m] = next.splice(from, 1); next.splice(to, 0, m);
    saveTasks(next); setDragTaskId(null); setDragOverTaskId(null);
  };

  const saveMS = (next) => { setMS(next); onUpdate(p.id, { milestones: next }); };
  const addMS  = () => { if (!newMS.trim()) return; saveMS([...milestones, { id: uid(), title: newMS.trim(), done: false }]); setNewMS(""); };
  const toggleMS = (id) => saveMS(milestones.map(m => m.id === id ? { ...m, done: !m.done } : m));
  const deleteMS = (id) => saveMS(milestones.filter(m => m.id !== id));

  const sendMessage = async () => {
    if (!msg.trim() || sending) return;
    setSending(true);
    try {
      const r = await apiFetch(`/infrastructure-projects/${p.id}/comments`, { method: "POST", body: JSON.stringify({ body: msg.trim() }) });
      const c = await r.json();
      setComments(prev => [...prev, c]);
      setMsg("");
    } catch (e) { console.error(e); } finally { setSending(false); }
  };

  const taskDone  = tasks.filter(t => t.done).length;
  const msDone    = milestones.filter(m => m.done).length;
  const totalItems = tasks.length + milestones.length;
  const totalDone  = taskDone + msDone;
  const pct        = totalItems ? Math.round(totalDone / totalItems * 100) : null;

  return (
    <div style={ mode === "full"
      ? { flex: 1, minWidth: 0, background: "var(--t-page)", display: "flex", flexDirection: "column", overflow: "hidden" }
      : { width: isMobile ? "100%" : 460, flexShrink: 0, borderLeft: isMobile ? "none" : "1px solid #1e293b", borderTop: isMobile ? "1px solid #1e293b" : "none", background: "var(--t-page)", display: "flex", flexDirection: "column", overflow: isMobile ? "visible" : "hidden" } }>
      {/* Header */}
      <div style={{ padding: "14px 16px 0", borderBottom: "1px solid var(--t-border-soft)", background: "var(--t-panel-hi)", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5, flexWrap: "wrap" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: pcfg.color || "#64748b", flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: pcfg.color || "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{p.status}</span>
              {p.client_name && <span style={{ fontSize: 11, color: "#475569" }}>· {p.client_name}</span>}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--t-text-primary)", lineHeight: 1.35, overflowWrap: "break-word" }}>{p.title}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {p.source_ticket_id && (
                <button onClick={() => navigate(`/tickets/${p.source_ticket_id}`)}
                  title={p.source_subject}
                  style={{ background: "rgba(37,99,235,0.1)", color: "#60a5fa", border: "1px solid rgba(37,99,235,0.25)", padding: "4px 10px", borderRadius: 99, fontWeight: 700, fontSize: 11, cursor: "pointer", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  🎫 TKT-{String(p.source_ticket_number || "").padStart(4, "0")}{p.source_ticket_status ? ` · ${p.source_ticket_status}` : ""}
                </button>
              )}
              {isAdmin && (
                <button onClick={() => navigate(`/projects/${p.id}`)}
                  title="Open the full admin-only project details page"
                  style={{ background: "var(--t-panel)", color: "#94a3b8", border: "1px solid var(--t-border-soft)", padding: "4px 10px", borderRadius: 99, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>
                  Full Details →
                </button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            <button onClick={onToggleSize}
              title={mode === "full" ? "Minimise to the side panel" : "Enlarge to full window"}
              style={{ background: "none", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 6, cursor: "pointer", padding: "3px 7px", lineHeight: 1, fontSize: 13 }}>
              {mode === "full" ? "⤡" : "⤢"}
            </button>
            <button onClick={onClose} title="Close"
              style={{ background: "none", border: "none", color: "#475569", fontSize: 20, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* Progress toward goal — goal text is clamped so a long goal can never
            push the layout off the panel (hover shows the full text) */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span title={goal || undefined} style={{ fontSize: 11, color: goal ? "#f59e0b" : "#64748b", fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {goal ? `🎯 ${goal}` : "Overall progress"}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: pct === 100 ? "#34d399" : "#94a3b8", flexShrink: 0 }}>{pct !== null ? `${pct}%` : "—"}</span>
          </div>
          <div style={{ height: 6, background: "var(--t-surface-mid)", borderRadius: 3, overflow: "hidden", position: "relative" }}>
            {pct !== null && (
              <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, transition: "width 0.4s",
                background: pct === 100 ? "#34d399" : `linear-gradient(90deg, #3b82f6, #8b5cf6)` }} />
            )}
          </div>
          {totalItems > 0 && (
            <div style={{ fontSize: 10, color: "#475569", marginTop: 3 }}>
              {totalDone}/{totalItems} items complete
              {tasks.length > 0 && ` · ${taskDone}/${tasks.length} tasks`}
              {milestones.length > 0 && ` · ${msDone}/${milestones.length} milestones`}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0 }}>
          {[
            { k: "overview", l: "Overview" },
            { k: "tasks",    l: `Tasks${tasks.length ? ` (${taskDone}/${tasks.length})` : ""}` },
            { k: "activity", l: `Chat${comments.length ? ` (${comments.length})` : ""}` },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} style={{
              padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
              background: "none", border: "none", fontFamily: "inherit",
              borderBottom: `2px solid ${tab === t.k ? "#3b82f6" : "transparent"}`,
              color: tab === t.k ? "#60a5fa" : "#64748b",
            }}>{t.l}</button>
          ))}
        </div>
      </div>

      {/* ── Overview Tab ── */}
      {tab === "overview" && (
        <div style={{ flex: isMobile ? "none" : 1, overflow: isMobile ? "visible" : "auto", padding: mode === "full" ? "18px 22px" : "14px 16px",
          display: mode === "full" && !isMobile ? "grid" : "flex",
          gridTemplateColumns: mode === "full" && !isMobile ? "repeat(auto-fit, minmax(320px, 1fr))" : undefined,
          alignItems: mode === "full" && !isMobile ? "start" : undefined,
          flexDirection: "column", gap: 12 }}>

          {/* Work timer — per-session time against this project */}
          <TimeTracker entityType="project" entityId={p.id} />

          {/* Milestone roadmap — vertical list. The old horizontal track pushed
              the 🎯 GOAL node off the panel once goals/milestones got long. */}
          <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
              Milestone Roadmap {milestones.length > 0 && <span style={{ color: "#475569", fontWeight: 600 }}>· {msDone}/{milestones.length}</span>}
            </div>

            {/* Vertical track */}
            <div style={{ position: "relative" }}>
              {(milestones.length > 0 || goal) && (
                <div style={{ position: "absolute", left: 9, top: 10, bottom: 10, width: 2, background: "var(--t-surface-mid)", borderRadius: 1 }} />
              )}
              {milestones.map(m => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", position: "relative" }}>
                  <button onClick={() => toggleMS(m.id)} title={m.done ? "Mark incomplete" : "Mark complete"}
                    style={{
                      width: 20, height: 20, borderRadius: "50%", cursor: "pointer", zIndex: 1,
                      background: m.done ? "#3b82f6" : "var(--t-surface-deep)",
                      border: `2.5px solid ${m.done ? "#3b82f6" : "#475569"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.2s", padding: 0, flexShrink: 0,
                    }}>
                    {m.done && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  </button>
                  <span title={m.title} style={{ flex: 1, minWidth: 0, fontSize: 12, color: m.done ? "#60a5fa" : "var(--t-text-body)", textDecoration: m.done ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.title}</span>
                  <button onClick={() => deleteMS(m.id)} title="Remove milestone" style={{ background: "none", border: "none", color: "var(--t-border-strong)", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>
              ))}
              {/* Goal — always the final stop on the track, never absolute-positioned */}
              {goal ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0 2px", position: "relative" }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%", zIndex: 1, flexShrink: 0,
                    background: milestones.length > 0 && msDone === milestones.length ? "#f59e0b" : "var(--t-surface-deep)",
                    border: "2.5px solid #f59e0b",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10,
                  }}>🎯</div>
                  <span title={goal} style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, color: "#f59e0b", overflowWrap: "break-word" }}>{goal}</span>
                </div>
              ) : null}
            </div>

            {/* Goal input */}
            <div style={{ margin: "12px 0" }}>
              <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>End Goal</label>
              <input value={goal} onChange={e => setGoal(e.target.value)} onBlur={() => { if ((goal || "") !== (p.goal || "")) onUpdate(p.id, { goal }); }}
                placeholder="e.g. Production deployment complete"
                style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 8px", color: "var(--badge-amber)", fontSize: 12, width: "100%", boxSizing: "border-box", fontWeight: 600 }} />
            </div>

            {/* Add milestone */}
            <div style={{ display: "flex", gap: 6 }}>
              <input value={newMS} onChange={e => setNewMS(e.target.value)} onKeyDown={e => e.key === "Enter" && addMS()}
                placeholder="Add milestone…"
                style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "5px 8px", color: "var(--t-text-primary)", fontSize: 11 }} />
              <button onClick={addMS} style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>+</button>
            </div>
          </div>

          {/* Status / Priority / Dates */}
          <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 9, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Status</label>
                <select value={p.status} onChange={e => onUpdate(p.id, { status: e.target.value })} style={ipt} disabled={updating}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Priority</label>
                <select value={p.priority} onChange={e => onUpdate(p.id, { priority: e.target.value })} style={ipt} disabled={updating}>
                  {["Critical","High","Medium","Low"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Start</label>
                {/* keyed by project + stored value: an uncontrolled date input kept showing the
                    PREVIOUS project's date after switching projects. Saves only real changes. */}
                <input type="date" key={`${p.id}-start-${p.start_date || ""}`} defaultValue={p.start_date?.split("T")[0] || ""}
                  onBlur={e => { const v = e.target.value || null; if (v !== (p.start_date?.split("T")[0] || null)) onUpdate(p.id, { start_date: v }); }} style={ipt} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Due</label>
                <input type="date" key={`${p.id}-due-${p.due_date || ""}`} defaultValue={p.due_date?.split("T")[0] || ""}
                  onBlur={e => { const v = e.target.value || null; if (v !== (p.due_date?.split("T")[0] || null)) onUpdate(p.id, { due_date: v }); }} style={ipt} />
              </div>
            </div>
          </div>

          {/* Team — editable. The first two members are synced to the linked
              ticket as primary/secondary tech; new members get email + Teams. */}
          <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Team</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#475569", textTransform: "none", letterSpacing: 0 }}>1st = primary · 2nd = secondary tech on the ticket</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {team.map((d, i) => (
                <div key={d.id} title={i === 0 ? "Primary tech on the linked ticket" : i === 1 ? "Secondary tech on the linked ticket" : d.name}
                  style={{ display: "flex", alignItems: "center", gap: 7, background: "var(--t-input)", border: `1px solid ${i === 0 ? "rgba(79,111,255,0.5)" : i === 1 ? "rgba(139,92,246,0.5)" : "var(--t-border-soft)"}`, borderRadius: 20, padding: "4px 8px 4px 4px" }}>
                  <Avatar name={d.name} size={22} />
                  <span style={{ fontSize: 12, color: "var(--t-text-body)" }}>{d.name}</span>
                  {i === 0 && <span style={{ fontSize: 9, fontWeight: 800, color: "#60a5fa" }}>P</span>}
                  {i === 1 && <span style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa" }}>S</span>}
                  <button onClick={() => removeMember(d.id)} title="Remove from team"
                    style={{ background: "none", border: "none", color: "var(--t-border-strong)", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
                </div>
              ))}
              {!addingMember ? (
                <button onClick={() => setAddingMember(true)}
                  style={{ background: "transparent", border: "1px dashed var(--t-border-strong)", color: "#64748b", borderRadius: 20, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  + Add
                </button>
              ) : (
                <select autoFocus value="" onChange={e => addMember(e.target.value)} onBlur={() => setAddingMember(false)}
                  style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 20, padding: "4px 8px", color: "var(--t-text-body)", fontSize: 12, cursor: "pointer" }}>
                  <option value="">Pick…</option>
                  {agents.filter(a => !teamIds.includes(a.id)).map(a => (
                    <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
                  ))}
                </select>
              )}
              {!team.length && !addingMember && <div style={{ fontSize: 12, color: "var(--t-border-strong)", alignSelf: "center" }}>Unassigned</div>}
            </div>
          </div>

          {/* Description */}
          {p.description && (
            <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 9, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Description</div>
              <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{p.description}</div>
            </div>
          )}

          {/* Notes */}
          <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 9, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Notes</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => { if ((notes || "") !== (p.notes || "")) onUpdate(p.id, { notes }); }}
              rows={3} placeholder="Add internal notes…"
              style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "7px 8px", color: "#f1f5f9", fontSize: 12, width: "100%", resize: "vertical", boxSizing: "border-box" }} />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 10, color: "var(--t-border-strong)" }}>Created {fmtL(p.created_at)}{p.created_by_name ? ` by ${p.created_by_name}` : ""}</div>
            <button onClick={() => onDelete(p.id)}
              style={{ background: "rgba(239,68,68,0.08)", color: "#f87171", border: "1px solid rgba(239,68,68,0.18)", borderRadius: 7, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              Delete Project
            </button>
          </div>
        </div>
      )}

      {/* ── Tasks Tab ── */}
      {tab === "tasks" && (
        <div style={{ flex: isMobile ? "none" : 1, overflow: isMobile ? "visible" : "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 11, color: "#475569" }}>Drag <span style={{ color: "var(--t-border-strong)" }}>⠿</span> to reorder · assigning a task notifies that person</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {tasks.map(t => {
              const isOver  = dragOverTaskId === t.id && dragTaskId !== t.id;
              const overdue = t.due && !t.done && new Date(t.due) < new Date();
              return (
                <div key={t.id} draggable
                  onDragStart={e => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; setDragTaskId(t.id); }}
                  onDragEnd={() => { setDragTaskId(null); setDragOverTaskId(null); }}
                  onDragOver={e => { e.preventDefault(); setDragOverTaskId(t.id); }}
                  onDragLeave={() => setDragOverTaskId(v => v === t.id ? null : v)}
                  onDrop={e => { e.preventDefault(); handleTaskDrop(t.id); }}
                  style={{ background: isOver ? "rgba(59,130,246,0.1)" : "var(--t-input)", border: `1px solid ${isOver ? "#3b82f6" : "var(--t-border-soft)"}`, borderRadius: 8, padding: "7px 9px", opacity: dragTaskId === t.id ? 0.4 : 1, transition: "all 0.1s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ color: "var(--t-border-strong)", cursor: "grab", fontSize: 12, flexShrink: 0, userSelect: "none" }}>⠿</span>
                    <button onClick={() => toggleTask(t.id)} style={{ width: 16, height: 16, borderRadius: 3, border: `2px solid ${t.done ? "#34d399" : "var(--t-border-strong)"}`, background: t.done ? "#34d399" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0 }}>
                      {t.done && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                    </button>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: t.done ? "#475569" : "var(--t-text-body)", textDecoration: t.done ? "line-through" : "none", overflowWrap: "break-word" }}>{t.title}</span>
                    <button onClick={() => deleteTask(t.id)} style={{ background: "none", border: "none", color: "var(--t-border-strong)", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1, flexShrink: 0 }}>×</button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, paddingLeft: 23 }}>
                    {t.assignee_name && <Avatar name={t.assignee_name} size={18} />}
                    <select value={t.assignee_id || ""} onChange={e => setTaskAssignee(t.id, e.target.value)}
                      style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 5, padding: "2px 5px", color: t.assignee_id ? "var(--t-text-body)" : "#64748b", fontSize: 10.5, cursor: "pointer", maxWidth: 130 }}>
                      <option value="">Unassigned</option>
                      {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      {agents.filter(a => !teamIds.includes(a.id)).map(a => (
                        <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
                      ))}
                    </select>
                    <input type="date" value={t.due || ""} onChange={e => setTaskDue(t.id, e.target.value)}
                      style={{ background: "var(--t-panel)", border: `1px solid ${overdue ? "rgba(239,68,68,0.5)" : "var(--t-border-soft)"}`, borderRadius: 5, padding: "1px 5px", color: overdue ? "#f87171" : "#94a3b8", fontSize: 10.5, cursor: "pointer" }} />
                    {overdue && <span style={{ fontSize: 10, color: "#f87171", fontWeight: 700 }}>overdue</span>}
                  </div>
                </div>
              );
            })}
            {tasks.length === 0 && (
              <div style={{ textAlign: "center", padding: 30, color: "var(--t-border-strong)" }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>📋</div>
                <div style={{ fontSize: 12, color: "#475569" }}>No tasks yet — add the first one below</div>
              </div>
            )}
          </div>
          {/* Composer */}
          <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 8, padding: 9, display: "flex", flexDirection: "column", gap: 6 }}>
            <input value={newTask} onChange={e => setNewTask(e.target.value)} onKeyDown={e => e.key === "Enter" && addTask()} placeholder="New task…"
              style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 8px", color: "var(--t-text-primary)", fontSize: 12 }} />
            <div style={{ display: "flex", gap: 6 }}>
              <select value={newTaskAssignee} onChange={e => setNewTaskAssignee(e.target.value)}
                style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "4px 6px", color: newTaskAssignee ? "var(--t-text-body)" : "#64748b", fontSize: 11, cursor: "pointer" }}>
                <option value="">Assign to…</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
              </select>
              <input type="date" value={newTaskDue} onChange={e => setNewTaskDue(e.target.value)}
                style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "4px 6px", color: "#94a3b8", fontSize: 11 }} />
              <button onClick={addTask} disabled={!newTask.trim()}
                style={{ background: newTask.trim() ? "#4f6fff" : "var(--t-surface-mid)", color: newTask.trim() ? "#fff" : "#64748b", border: "none", borderRadius: 6, padding: "4px 14px", fontSize: 12, fontWeight: 700, cursor: newTask.trim() ? "pointer" : "default" }}>
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Activity Tab ── */}
      {tab === "activity" && (
        <div style={{ flex: isMobile ? "none" : 1, display: "flex", flexDirection: "column", overflow: isMobile ? "visible" : "hidden" }}>
          {/* Feed */}
          <div ref={feedRef} style={{ flex: isMobile ? "none" : 1, overflow: isMobile ? "visible" : "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 0 }}>
            {commLoad && <div style={{ textAlign: "center", padding: 24, color: "#475569", fontSize: 13 }}>Loading…</div>}
            {!commLoad && comments.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: "var(--t-border-strong)" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
                <div style={{ fontSize: 13, color: "#475569" }}>No messages yet</div>
                <div style={{ fontSize: 12, color: "var(--t-border-strong)", marginTop: 4 }}>Start the conversation below</div>
              </div>
            )}
            {comments.map((c, i) => {
              const prev = comments[i - 1];
              const sameSender = prev?.agent_id === c.agent_id && !prev?.is_system;
              const ts = new Date(c.created_at);
              const timeStr = ts.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
              const dateStr = ts.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              const showDate = !prev || new Date(prev.created_at).toDateString() !== ts.toDateString();
              return (
                <div key={c.id}>
                  {showDate && (
                    <div style={{ textAlign: "center", margin: "12px 0 8px", fontSize: 11, color: "var(--t-border-strong)", display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 1, background: "var(--t-surface-mid)" }} />
                      {dateStr}
                      <div style={{ flex: 1, height: 1, background: "var(--t-surface-mid)" }} />
                    </div>
                  )}
                  {c.is_system ? (
                    <div style={{ textAlign: "center", margin: "6px 0", fontSize: 11, color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, height: 1, background: "var(--t-surface-mid)" }} />
                      <span style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 99, padding: "2px 10px" }}>{c.body}</span>
                      <div style={{ flex: 1, height: 1, background: "var(--t-surface-mid)" }} />
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: sameSender ? 3 : 10 }}>
                      {!sameSender ? <Avatar name={c.agent_name || "?"} size={28} /> : <div style={{ width: 28, flexShrink: 0 }} />}
                      <div style={{ flex: 1 }}>
                        {!sameSender && (
                          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--t-text-body)" }}>{c.agent_name || "Unknown"}</span>
                            {c.source && c.source !== "web" && (
                              <span title={`Sent from ${c.source}`} style={{
                                fontSize: 9, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase",
                                padding: "1px 6px", borderRadius: 99,
                                background: c.source === "teams" ? "rgba(98,100,167,0.2)" : "rgba(251,191,36,0.15)",
                                color: c.source === "teams" ? "#a5a8ff" : "#fbbf24",
                              }}>
                                {c.source === "teams" ? "Teams" : "Email"}
                              </span>
                            )}
                            <span style={{ fontSize: 10, color: "#475569" }}>{timeStr}</span>
                          </div>
                        )}
                        <div style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: sameSender ? "4px 12px 12px 4px" : "4px 12px 12px 12px", padding: "8px 11px", fontSize: 13, color: "var(--t-text-body)", lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {c.body}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Message input */}
          <div style={{ padding: "10px 14px", borderTop: "1px solid var(--t-border-soft)", background: "var(--t-panel-hi)", flexShrink: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                value={msg}
                onChange={e => setMsg(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Message the team… (Enter to send · relays to Teams + email, and replies from either land back here)"
                rows={2}
                style={{ flex: 1, background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 8, padding: "8px 10px", color: "#f1f5f9", fontSize: 13, resize: "none", outline: "none", lineHeight: 1.5 }}
              />
              <button onClick={sendMessage} disabled={!msg.trim() || sending}
                style={{ background: msg.trim() ? "#4f6fff" : "#1e293b", color: msg.trim() ? "#fff" : "#475569", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: msg.trim() ? "pointer" : "default", transition: "all 0.15s", flexShrink: 0 }}>
                {sending ? "…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New Project Modal ───────────────────────────────────────────────────────
function NewProjectModal({ agents, clients, onClose, onCreated }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ title: "", description: "", client_id: "", priority: "Medium", start_date: "", due_date: "", notes: "", goal: "" });
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const ipt = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 12px", color: "#f1f5f9", fontSize: 13, width: "100%", boxSizing: "border-box" };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setErr("Title is required"); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/infrastructure-projects", {
        method: "POST",
        body: JSON.stringify({ ...form, assigned_to: selected }),
      });
      if (!r.ok) { const d = await r.json(); setErr(d.error || "Failed"); return; }
      onCreated();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 16 : 0 }}>
      <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 14, width: isMobile ? "100%" : 520, maxWidth: 520, maxHeight: "85vh", overflow: "auto", boxShadow: "0 25px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>New Project</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171", borderRadius: 7, padding: "8px 12px", fontSize: 13 }}>{err}</div>}
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Project Title *</label>
            <input type="text" placeholder="e.g. Office Network Upgrade" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={ipt} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Description</label>
            <textarea rows={3} placeholder="Scope and objectives…" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={ipt} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>🎯 End Goal</label>
            <input type="text" placeholder="e.g. Production deployment complete" value={form.goal} onChange={e => setForm(p => ({ ...p, goal: e.target.value }))} style={ipt} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Client</label>
              <select value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} style={ipt}>
                <option value="">No client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Priority</label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} style={ipt}>
                {["Critical","High","Medium","Low"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} style={ipt} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} style={ipt} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 8, fontWeight: 600 }}>Assign Team Members</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {agents.map(a => {
                const name = `${a.first_name} ${a.last_name}`;
                const picked = selected.indexOf(a.id);
                return (
                  <button key={a.id} onClick={() => setSelected(s => picked >= 0 ? s.filter(x => x !== a.id) : [...s, a.id])}
                    title={picked === 0 ? "Primary tech on the ticket" : picked === 1 ? "Secondary tech on the ticket" : undefined}
                    style={{ background: picked >= 0 ? "rgba(29,78,216,0.2)" : "var(--t-panel)", border: `1px solid ${picked >= 0 ? "#2563eb" : "var(--t-border-soft)"}`, borderRadius: 99, padding: "4px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    <Avatar name={name} size={20} />
                    <span style={{ fontSize: 12, color: picked >= 0 ? "#93c5fd" : "#94a3b8", fontWeight: picked >= 0 ? 600 : 400 }}>{name}</span>
                    {picked === 0 && <span style={{ fontSize: 9, fontWeight: 800, color: "#60a5fa" }}>P</span>}
                    {picked === 1 && <span style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa" }}>S</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 10, background: "rgba(79,111,255,0.06)", border: "1px solid rgba(79,111,255,0.15)", borderRadius: 7, padding: "8px 10px", lineHeight: 1.5 }}>
              🎫 Creating the project also creates a linked tracking ticket. The whole team is briefed by email + Teams;
              the first member picked becomes the ticket's <b>primary</b> tech, the second its <b>secondary</b>.
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--t-border-soft)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "none", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 18px", color: "#64748b", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ background: saving ? "#1e3a8a" : "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer" }}>
            {saving ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Maps View ───────────────────────────────────────────────────────────────
function MapsView({ maps, loading, clients, agents, activeMap, setActiveMap, showNewMap, setShowNewMap, onReload, notify }) {
  const isMobile = useIsMobile();
  // What was LOADED for the open map. It only changes when a map is opened: the canvas
  // reports edits up, and feeding them back down here re-initialised the canvas on every
  // edit — which wiped the undo history, so Undo/Redo never lit up.
  const [canvasData, setCanvasData] = useState({ nodes: [], edges: [] });
  const [saveState, setSaveState] = useState("saved");   // saved | pending | saving | error
  const [filterClient, setFilterClient] = useState("");
  const saveTimeout = useRef(null);
  const pendingSave = useRef(null);                      // { mapId, data } still to be written
  const saving      = useRef(Promise.resolve());

  // One save at a time: a slow save still on its way could otherwise land AFTER a newer one
  // and put the older diagram back. Each save sends whatever is newest when its turn comes.
  const flushSave = useCallback(() => {
    saving.current = saving.current.then(async () => {
      const job = pendingSave.current;
      if (!job) return;
      setSaveState("saving");
      try {
        const r = await apiFetch(`/infrastructure-maps/${job.mapId}`, { method: "PATCH", body: JSON.stringify({ canvas_data: job.data }) });
        if (!r.ok) throw new Error(String(r.status));
        if (pendingSave.current === job) { pendingSave.current = null; setSaveState("saved"); }
      } catch { setSaveState("error"); }
    });
    return saving.current;
  }, []);

  const saveCanvas = useCallback((mapId, data) => {
    if (!mapId) return;
    pendingSave.current = { mapId, data };
    setSaveState("pending");
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(flushSave, 800);
  }, [flushSave]);

  const openMap = async (map) => {
    if (activeMap?.id === map.id) return;                // already open — reloading would drop unsaved edits
    if (pendingSave.current) { clearTimeout(saveTimeout.current); await flushSave(); }
    const r   = await apiFetch(`/infrastructure-maps/${map.id}`);
    if (!r.ok) { notify?.("Couldn't open that diagram — please try again."); return; }
    const full = await r.json();
    setActiveMap(full);
    setCanvasData(full.canvas_data || { nodes: [], edges: [] });
    setSaveState("saved");
  };

  const handleCanvasChange = (data) => {
    if (activeMap) saveCanvas(activeMap.id, data);
  };

  const handleDeleteMap = async (id) => {
    if (!window.confirm("Delete this diagram?")) return;
    const r = await apiFetch(`/infrastructure-maps/${id}`, { method: "DELETE" }).catch(() => null);
    if (!r?.ok) {
      notify?.(r?.status === 403 ? "Only admins and managers can delete diagrams." : "Couldn't delete the diagram — please try again.");
      return;
    }
    if (activeMap?.id === id) { pendingSave.current = null; clearTimeout(saveTimeout.current); setActiveMap(null); setCanvasData({ nodes: [], edges: [] }); }
    onReload();
  };

  const filteredMaps = filterClient ? maps.filter(m => m.client_id === filterClient) : maps;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "visible" : "hidden" }}>
      {/* Left sidebar */}
      <div style={{ width: isMobile ? "100%" : 240, flexShrink: 0, maxHeight: isMobile ? 240 : undefined, borderRight: isMobile ? "none" : "1px solid rgba(148,163,184,0.11)", borderBottom: isMobile ? "1px solid rgba(148,163,184,0.11)" : "none", overflow: "auto", background: "var(--t-panel-hi)" }}>
        <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--t-border-soft)" }}>
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            style={{ background: "var(--t-page)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "5px 8px", color: "#cbd5e1", fontSize: 12, width: "100%" }}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        {loading && <div style={{ padding: 20, color: "#475569", fontSize: 13 }}>Loading…</div>}
        {!loading && filteredMaps.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--t-border-strong)", fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🗺️</div>No diagrams yet
          </div>
        )}
        {filteredMaps.map(m => (
          <div key={m.id} onClick={() => openMap(m)}
            style={{ padding: "11px 14px", cursor: "pointer", borderBottom: "1px solid #111827", background: activeMap?.id === m.id ? "var(--t-surface-mid)" : "transparent", borderLeft: `3px solid ${activeMap?.id === m.id ? "#2563eb" : "transparent"}`, transition: "all 0.1s" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: activeMap?.id === m.id ? "#93c5fd" : "#e2e8f0", marginBottom: 3 }}>{m.title}</div>
            {m.client_name && <div style={{ fontSize: 11, color: "#475569" }}>{m.client_name}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
              <span style={{ fontSize: 10, color: "var(--t-border-strong)", background: "var(--t-panel)", borderRadius: 4, padding: "2px 6px", textTransform: "capitalize" }}>{m.map_type}</span>
              <button onClick={e => { e.stopPropagation(); handleDeleteMap(m.id); }}
                style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, padding: "0 2px", lineHeight: 1 }}>🗑</button>
            </div>
          </div>
        ))}
      </div>

      {/* Canvas area */}
      <div style={{ flex: isMobile ? "none" : 1, height: isMobile ? "65vh" : undefined, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {!activeMap && (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "#475569" }}>
            <div style={{ fontSize: 48 }}>🗺️</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#64748b" }}>Select a diagram or create one</div>
            <button onClick={() => setShowNewMap(true)}
              style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 6 }}>
              + New Diagram
            </button>
          </div>
        )}
        {activeMap && (
          <>
            <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: "var(--t-panel-hi)" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{activeMap.title}</div>
                {activeMap.client_name && <div style={{ fontSize: 11, color: "#64748b" }}>{activeMap.client_name}</div>}
              </div>
              {saveState === "error" ? (
                <span style={{ fontSize: 11, color: "#f87171", marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 8 }}>
                  ⚠ Couldn't save
                  <button onClick={flushSave} style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Retry</button>
                </span>
              ) : (
                <span style={{ fontSize: 11, color: saveState === "saved" ? "#22c55e" : "#64748b", marginLeft: 8 }}>
                  {saveState === "saved" ? "✓ Saved" : saveState === "saving" ? "Saving…" : "Unsaved changes…"}
                </span>
              )}
            </div>
            <MapCanvas canvasData={canvasData} onChange={handleCanvasChange} mapTitle={activeMap.title} />
          </>
        )}
      </div>

      {showNewMap && (
        <NewMapModal clients={clients}
          onClose={() => setShowNewMap(false)}
          onCreated={async (map) => { setShowNewMap(false); await onReload(); await openMap(map); }}
        />
      )}
    </div>
  );
}

// ─── Map Canvas ──────────────────────────────────────────────────────────────
function MapCanvas({ canvasData, onChange, mapTitle }) {
  const isMobile = useIsMobile();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  // View transform
  const [xfm, setXfm]     = useState({ scale: 1, tx: 50, ty: 50 });
  const [snapOn, setSnap]  = useState(true);

  // Interaction modes & state
  const [mode, setMode]         = useState("select");
  const [selNodes, setSelNodes] = useState(new Set());
  const [selEdge, setSelEdge]   = useState(null);
  const [connectFrom, setCF]    = useState(null);
  const [dragging, setDragging] = useState(null);
  const [rubberBand, setRB]     = useState(null);
  const [panState, setPan]      = useState(null); // { startX, startY, startTx, startTy }
  const [spaceDown, setSpace]   = useState(false);
  const [canUndo, setCanUndo]   = useState(false);
  const [canRedo, setCanRedo]   = useState(false);

  // History stored in refs to avoid stale closures
  const histRef  = useRef([]);
  const histIdx  = useRef(-1);
  const clipRef  = useRef(null);
  const wrapRef  = useRef(null); // outer container
  const nodesRef = useRef([]);   // latest nodes, so the end of a drag can save them without a state-updater side effect
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  useEffect(() => {
    const n = canvasData.nodes || [];
    const e = canvasData.edges || [];
    setNodes(n); setEdges(e);
    histRef.current = [{ nodes: n, edges: e }];
    histIdx.current = 0;
    setCanUndo(false); setCanRedo(false);
  }, [canvasData]);

  const toCanvas = useCallback((sx, sy) => {
    if (!wrapRef.current) return { x: sx, y: sy };
    const r = wrapRef.current.getBoundingClientRect();
    return {
      x: (sx - r.left - xfm.tx) / xfm.scale,
      y: (sy - r.top  - xfm.ty) / xfm.scale,
    };
  }, [xfm]);

  const emit = useCallback((n, e) => {
    const idx = histIdx.current;
    const next = histRef.current.slice(0, idx + 1);
    next.push({ nodes: n, edges: e });
    if (next.length > 80) next.shift();
    histRef.current = next;
    histIdx.current = next.length - 1;
    setCanUndo(histIdx.current > 0);
    setCanRedo(false);
    onChange({ nodes: n, edges: e });
  }, [onChange]);

  const undo = useCallback(() => {
    if (histIdx.current <= 0) return;
    histIdx.current--;
    const s = histRef.current[histIdx.current];
    setNodes(s.nodes); setEdges(s.edges);
    onChange({ nodes: s.nodes, edges: s.edges });
    setSelNodes(new Set()); setSelEdge(null);
    setCanUndo(histIdx.current > 0);
    setCanRedo(true);
  }, [onChange]);

  const redo = useCallback(() => {
    if (histIdx.current >= histRef.current.length - 1) return;
    histIdx.current++;
    const s = histRef.current[histIdx.current];
    setNodes(s.nodes); setEdges(s.edges);
    onChange({ nodes: s.nodes, edges: s.edges });
    setCanUndo(true);
    setCanRedo(histIdx.current < histRef.current.length - 1);
  }, [onChange]);

  const handleWheel = useCallback((ev) => {
    ev.preventDefault();
    if (ev.ctrlKey || ev.metaKey) {
      const r = wrapRef.current.getBoundingClientRect();
      const mx = ev.clientX - r.left;
      const my = ev.clientY - r.top;
      const factor = ev.deltaY < 0 ? 1.12 : 0.88;
      setXfm(t => {
        const ns = Math.max(0.15, Math.min(4, t.scale * factor));
        const ratio = ns / t.scale;
        return { scale: ns, tx: mx - ratio * (mx - t.tx), ty: my - ratio * (my - t.ty) };
      });
    } else {
      setXfm(t => ({ ...t, tx: t.tx - ev.deltaX, ty: t.ty - ev.deltaY }));
    }
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handleMouseDown = useCallback((ev) => {
    if (ev.button === 1 || (ev.button === 0 && spaceDown)) {
      ev.preventDefault();
      setPan({ startX: ev.clientX, startY: ev.clientY, startTx: xfm.tx, startTy: xfm.ty });
      return;
    }
  }, [spaceDown, xfm]);

  const handleCanvasMouseDown = useCallback((ev) => {
    if (ev.button === 1 || (ev.button === 0 && spaceDown)) return;
    if (ev.button !== 0) return;
    if (mode === "select") {
      const cp = toCanvas(ev.clientX, ev.clientY);
      setRB({ sx: cp.x, sy: cp.y, ex: cp.x, ey: cp.y });
      setSelNodes(new Set()); setSelEdge(null);
    }
  }, [mode, spaceDown, toCanvas]);

  const handleMouseMove = useCallback((ev) => {
    if (panState) {
      setXfm(t => ({
        ...t,
        tx: panState.startTx + (ev.clientX - panState.startX),
        ty: panState.startTy + (ev.clientY - panState.startY),
      }));
      return;
    }
    if (dragging && !spaceDown) {
      const cp = toCanvas(ev.clientX, ev.clientY);
      const rawX = cp.x - dragging.ox;
      const rawY = cp.y - dragging.oy;
      const nx = snap(rawX, snapOn);
      const ny = snap(rawY, snapOn);
      if (!dragging.moved) setDragging(d => (d ? { ...d, moved: true } : d));
      setNodes(prev => {
        if (dragging.multi && selNodes.size > 1) {
          const dx = nx - dragging.baseX;
          const dy = ny - dragging.baseY;
          return prev.map(n => selNodes.has(n.id) ? { ...n, x: n.x + dx, y: n.y + dy } : n);
        }
        return prev.map(n => n.id === dragging.nodeId ? { ...n, x: nx, y: ny } : n);
      });
      if (dragging.multi && selNodes.size > 1) {
        setDragging(d => ({ ...d, baseX: nx, baseY: ny }));
      }
      return;
    }
    if (rubberBand) {
      const cp = toCanvas(ev.clientX, ev.clientY);
      setRB(rb => ({ ...rb, ex: cp.x, ey: cp.y }));
    }
  }, [panState, dragging, spaceDown, toCanvas, snapOn, rubberBand, selNodes]);

  const handleMouseUp = useCallback((ev) => {
    if (panState) { setPan(null); return; }
    if (dragging) {
      // Only a real move is an edit — a plain click on a node no longer adds an undo step and a save.
      if (dragging.moved) emit(nodesRef.current, edges);
      setDragging(null);
      return;
    }
    if (rubberBand) {
      const minX = Math.min(rubberBand.sx, rubberBand.ex);
      const maxX = Math.max(rubberBand.sx, rubberBand.ex);
      const minY = Math.min(rubberBand.sy, rubberBand.ey);
      const maxY = Math.max(rubberBand.sy, rubberBand.ey);
      if (maxX - minX > 4 || maxY - minY > 4) {
        const sel = new Set(nodes
          .filter(n => {
            const nw = n.w || NODE_W, nh = n.h || NODE_H;
            return n.x < maxX && n.x + nw > minX && n.y < maxY && n.y + nh > minY;
          })
          .map(n => n.id));
        setSelNodes(sel);
      }
      setRB(null);
    }
  }, [panState, dragging, edges, emit, rubberBand, nodes]);

  const handleCanvasClick = useCallback((ev) => {
    if (spaceDown || panState) return;
    if (mode.startsWith("add:")) {
      const cp = toCanvas(ev.clientX, ev.clientY);
      const type = mode.split(":")[1];
      const cfg  = NODE_TYPES[type] || NODE_TYPES.server;
      const nw   = type === "diamond" ? 100 : NODE_W;
      const nh   = type === "diamond" ? 100 : (type === "note" ? 90 : NODE_H);
      const node = { id: uid(), type, label: cfg.label, x: snap(cp.x - nw/2, snapOn), y: snap(cp.y - nh/2, snapOn), props: {}, w: nw, h: nh };
      const next = [...nodes, node];
      setNodes(next); emit(next, edges);
      setMode("select");
    }
  }, [spaceDown, panState, mode, toCanvas, nodes, edges, emit, snapOn]);

  const handleNodeMouseDown = useCallback((ev, nodeId) => {
    ev.stopPropagation();
    if (spaceDown || panState) return;
    if (mode === "connect") {
      if (!connectFrom) { setCF(nodeId); }
      else if (connectFrom !== nodeId) {
        const edge = { id: uid(), from: connectFrom, to: nodeId, label: "", type: "straight" };
        const next = [...edges, edge];
        setEdges(next); emit(nodes, next);
        setCF(null); setMode("select");
      }
      return;
    }
    const isInSel = selNodes.has(nodeId);
    if (ev.shiftKey) {
      setSelNodes(s => { const n = new Set(s); n.has(nodeId) ? n.delete(nodeId) : n.add(nodeId); return n; });
      setSelEdge(null);
    } else {
      if (!isInSel) { setSelNodes(new Set([nodeId])); setSelEdge(null); }
    }
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const cp = toCanvas(ev.clientX, ev.clientY);
    const multi = (isInSel || ev.shiftKey) && selNodes.size > 1;
    setDragging({ nodeId, ox: cp.x - node.x, oy: cp.y - node.y, multi, baseX: snap(cp.x - node.x, snapOn), baseY: snap(cp.y - node.y, snapOn) });
  }, [spaceDown, panState, mode, connectFrom, selNodes, nodes, edges, emit, toCanvas, snapOn]);

  const handleKeyDown = useCallback((ev) => {
    // Typing in the properties panel comes first: the space shortcut (pan) used to run before
    // this check and swallowed every space typed into a label, IP, hostname, OS or notes field.
    const tag = ev.target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    // Letters compared lower-case: with Shift (Ctrl+Shift+Z = redo) or Caps Lock on, the key is "Z"/"A"…,
    // so redo by Ctrl/Cmd+Shift+Z never worked and Caps Lock turned every shortcut off.
    const k = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
    if (ev.key === " ") { ev.preventDefault(); setSpace(true); return; }
    if ((ev.ctrlKey || ev.metaKey) && k === "z" && !ev.shiftKey) { ev.preventDefault(); undo(); return; }
    if ((ev.ctrlKey || ev.metaKey) && (k === "y" || (k === "z" && ev.shiftKey))) { ev.preventDefault(); redo(); return; }
    if ((ev.ctrlKey || ev.metaKey) && k === "c") {
      const copied = nodes.filter(n => selNodes.has(n.id));
      if (copied.length) clipRef.current = copied.map(n => ({ ...n, props: { ...n.props } }));
      return;
    }
    if ((ev.ctrlKey || ev.metaKey) && k === "v") {
      if (!clipRef.current) return;
      const pasted = clipRef.current.map(n => ({ ...n, id: uid(), x: n.x + 30, y: n.y + 30 }));
      const next = [...nodes, ...pasted];
      setNodes(next); emit(next, edges);
      setSelNodes(new Set(pasted.map(n => n.id)));
      return;
    }
    if ((ev.ctrlKey || ev.metaKey) && k === "a") {
      ev.preventDefault();
      setSelNodes(new Set(nodes.map(n => n.id)));
      return;
    }
    if (ev.key === "Delete" || ev.key === "Backspace") {
      if (selNodes.size > 0) {
        const nextN = nodes.filter(n => !selNodes.has(n.id));
        const nextE = edges.filter(e => !selNodes.has(e.from) && !selNodes.has(e.to));
        setNodes(nextN); setEdges(nextE); emit(nextN, nextE); setSelNodes(new Set());
      } else if (selEdge) {
        const nextE = edges.filter(e => e.id !== selEdge);
        setEdges(nextE); emit(nodes, nextE); setSelEdge(null);
      }
    }
    if (ev.key === "Escape") { setMode("select"); setCF(null); setSelNodes(new Set()); setSelEdge(null); }
  }, [undo, redo, nodes, edges, emit, selNodes, selEdge]);

  const handleKeyUp = useCallback((ev) => {
    if (ev.key === " ") setSpace(false);
  }, []);

  const selNode = selNodes.size === 1 ? nodes.find(n => n.id === [...selNodes][0]) : null;
  const selEdgeObj = selEdge ? edges.find(e => e.id === selEdge) : null;

  const updateNodeProp = (field, val) => {
    const next = nodes.map(n => selNodes.has(n.id) ? { ...n, [field]: val } : n);
    setNodes(next); emit(next, edges);
  };
  const updateNodeIProp = (field, val) => {
    const next = nodes.map(n => selNodes.has(n.id) ? { ...n, props: { ...n.props, [field]: val } } : n);
    setNodes(next); emit(next, edges);
  };
  const updateEdge = (field, val) => {
    const next = edges.map(e => e.id === selEdge ? { ...e, [field]: val } : e);
    setEdges(next); emit(nodes, next);
  };

  const exportPNG = useCallback(() => {
    if (!nodes.length) return;
    const pad = 40;
    const minX = Math.min(...nodes.map(n => n.x)) - pad;
    const minY = Math.min(...nodes.map(n => n.y)) - pad;
    const maxX = Math.max(...nodes.map(n => n.x + (n.w || NODE_W))) + pad;
    const maxY = Math.max(...nodes.map(n => n.y + (n.h || NODE_H))) + pad;
    const cv = document.createElement("canvas");
    cv.width = maxX - minX; cv.height = maxY - minY;
    const ctx = cv.getContext("2d");
    // A canvas can't read CSS variables ("var(--t-page)" was ignored → black background):
    // use the page colour the browser actually computed.
    const pageBg = wrapRef.current ? getComputedStyle(wrapRef.current).backgroundColor : "";
    ctx.fillStyle = pageBg && pageBg !== "rgba(0, 0, 0, 0)" && pageBg !== "transparent" ? pageBg : "#0b1220";
    ctx.fillRect(0, 0, cv.width, cv.height);
    // Grid dots
    ctx.fillStyle = "#cbd5e1";
    for (let gx = 0; gx < cv.width; gx += GRID_SNAP)
      for (let gy = 0; gy < cv.height; gy += GRID_SNAP) {
        ctx.beginPath(); ctx.arc(gx, gy, 1, 0, Math.PI * 2); ctx.fill();
      }
    // Edges
    edges.forEach(e => {
      const fn = nodes.find(n => n.id === e.from);
      const tn = nodes.find(n => n.id === e.to);
      if (!fn || !tn) return;
      const { x1, y1, x2, y2 } = getAnchor(fn, tn);
      ctx.strokeStyle = "#475569"; ctx.lineWidth = 2;
      ctx.beginPath();
      if (e.type === "curved") {
        const dx = (x2 - x1) * 0.55;
        ctx.moveTo(x1 - minX, y1 - minY);
        ctx.bezierCurveTo(x1 + dx - minX, y1 - minY, x2 - dx - minX, y2 - minY, x2 - minX, y2 - minY);
      } else {
        ctx.moveTo(x1 - minX, y1 - minY); ctx.lineTo(x2 - minX, y2 - minY);
      }
      ctx.stroke();
    });
    // Nodes — same colours as on screen. Theme colours ("var(--badge-amber)") are looked up on the page.
    const css  = getComputedStyle(wrapRef.current || document.documentElement);
    const real = c => { const m = /^var\((--[\w-]+)\)$/.exec(c || ""); return m ? (css.getPropertyValue(m[1]).trim() || "#94a3b8") : c; };
    nodes.forEach(n => {
      const cfg = NODE_TYPES[n.type] || NODE_TYPES.server;
      const nw = n.w || NODE_W, nh = n.h || NODE_H;
      const nx = n.x - minX, ny = n.y - minY;
      const note   = cfg.shape === "note";
      const fillC  = n.props?.fillColor   || (note ? "#fef3c7" : real(cfg.color));
      const bordC  = real(n.props?.borderColor || cfg.color);
      const textC  = n.props?.textColor   || (note ? "#92400e" : "#e2e8f0");
      ctx.fillStyle = fillC; ctx.strokeStyle = bordC; ctx.lineWidth = 2;
      ctx.beginPath();
      if (cfg.shape === "rounded" || note) {
        ctx.roundRect(nx, ny, nw, nh, 14);
      } else {
        ctx.roundRect(nx, ny, nw, nh, 6);
      }
      ctx.globalAlpha = n.props?.fillColor || note ? 1 : 0.13;
      ctx.fill(); ctx.globalAlpha = 1; ctx.stroke();
      ctx.fillStyle = textC;
      ctx.font = `bold 12px system-ui`;
      ctx.textAlign = "center";
      ctx.fillText(n.label, nx + nw / 2, ny + nh / 2 + 4);
    });
    const a = document.createElement("a");
    a.download = `${mapTitle || "diagram"}.png`;
    a.href = cv.toDataURL("image/png");
    a.click();
  }, [nodes, edges, mapTitle]);

  const zoom = (delta) => setXfm(t => ({ ...t, scale: Math.max(0.15, Math.min(4, t.scale + delta)) }));
  const resetView = () => setXfm({ scale: 1, tx: 50, ty: 50 });

  const cursor = spaceDown ? (panState ? "grabbing" : "grab") : (mode === "connect" ? "crosshair" : mode.startsWith("add:") ? "cell" : "default");

  const ipt = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 8px", color: "#f1f5f9", fontSize: 12, width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: "hidden", position: "relative" }}
      onKeyDown={handleKeyDown} onKeyUp={handleKeyUp} tabIndex={0}
      onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>

      {/* Left toolbar */}
      <div style={{ width: isMobile ? "100%" : 152, flexShrink: 0, maxHeight: isMobile ? 150 : undefined, borderRight: isMobile ? "none" : "1px solid rgba(148,163,184,0.11)", borderBottom: isMobile ? "1px solid rgba(148,163,184,0.11)" : "none", background: "var(--t-panel-hi)", overflow: "auto", padding: "10px 8px", display: "flex", flexDirection: isMobile ? "row" : "column", flexWrap: isMobile ? "wrap" : "nowrap", alignItems: isMobile ? "center" : "stretch", gap: 3, zIndex: 10 }}>
        {/* Zoom controls */}
        <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
          <button onClick={() => zoom(0.15)} title="Zoom in" style={tbBtn(false)}> + </button>
          <button onClick={() => zoom(-0.15)} title="Zoom out" style={tbBtn(false)}> − </button>
          <button onClick={resetView} title="Reset view" style={{ ...tbBtn(false), flex: 1, fontSize: 10 }}>
            {Math.round(xfm.scale * 100)}%
          </button>
        </div>
        <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
          <button onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)" style={{ ...tbBtn(false, canUndo ? undefined : "var(--t-border-strong)"), flex: 1, opacity: canUndo ? 1 : 0.4 }}>↩</button>
          <button onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Y)" style={{ ...tbBtn(false, canRedo ? undefined : "var(--t-border-strong)"), flex: 1, opacity: canRedo ? 1 : 0.4 }}>↪</button>
          <button onClick={exportPNG} title="Export PNG" style={{ ...tbBtn(false), flex: 1, fontSize: 11 }}>PNG</button>
        </div>
        <button onClick={() => setSnap(s => !s)} style={tbBtn(snapOn, snapOn ? "#10b981" : undefined)} title="Snap to grid">
          {snapOn ? "⊞ Snap On" : "⊟ Snap Off"}
        </button>
        <div style={{ height: 1, background: "var(--t-border-soft)", margin: "6px 0" }} />

        <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", paddingLeft: 2 }}>Network</div>
        {["server","workstation","switch","router","firewall","cloud","database","lb"].map(type => {
          const cfg = NODE_TYPES[type];
          return (
            <button key={type} onClick={() => { setMode(`add:${type}`); setSelNodes(new Set()); }}
              style={{ background: mode === `add:${type}` ? tint(cfg.color, 15) : "transparent", border: `1px solid ${mode === `add:${type}` ? cfg.color : "var(--t-border-soft)"}`, color: mode === `add:${type}` ? cfg.color : "#94a3b8", borderRadius: 6, padding: "5px 7px", fontSize: 11, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 6, transition: "all 0.1s" }}>
              <span style={{ background: tint(cfg.color, 20), color: cfg.color, borderRadius: 4, padding: "1px 4px", fontSize: 9, fontWeight: 800, fontFamily: "monospace", flexShrink: 0 }}>{cfg.symbol}</span>
              {cfg.label}
            </button>
          );
        })}

        <div style={{ height: 1, background: "var(--t-border-soft)", margin: "4px 0" }} />
        <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", paddingLeft: 2 }}>Shapes</div>
        {["note","box","diamond","person"].map(type => {
          const cfg = NODE_TYPES[type];
          return (
            <button key={type} onClick={() => { setMode(`add:${type}`); setSelNodes(new Set()); }}
              style={{ background: mode === `add:${type}` ? tint(cfg.color, 15) : "transparent", border: `1px solid ${mode === `add:${type}` ? cfg.color : "var(--t-border-soft)"}`, color: mode === `add:${type}` ? cfg.color : "#94a3b8", borderRadius: 6, padding: "5px 7px", fontSize: 11, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 6, transition: "all 0.1s" }}>
              <span style={{ background: tint(cfg.color, 20), color: cfg.color, borderRadius: 4, padding: "1px 4px", fontSize: 10, flexShrink: 0 }}>{cfg.symbol}</span>
              {cfg.label}
            </button>
          );
        })}

        <div style={{ height: 1, background: "var(--t-border-soft)", margin: "4px 0" }} />
        <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", paddingLeft: 2 }}>Tools</div>
        <button onClick={() => { setMode("select"); setCF(null); }} style={tbBtn(mode === "select")}>↖ Select</button>
        <button onClick={() => { setMode("connect"); setSelNodes(new Set()); }} style={tbBtn(mode === "connect", "#f59e0b")}>⟶ Connect</button>
        {connectFrom && (
          <div style={{ fontSize: 11, color: "var(--badge-amber)", padding: "4px 6px", background: "rgba(251,191,36,0.1)", borderRadius: 6, lineHeight: 1.5 }}>
            Click target to connect
          </div>
        )}
        {!isMobile && (
          <>
            <div style={{ height: 1, background: "var(--t-border-soft)", margin: "4px 0" }} />
            <div style={{ fontSize: 9, color: "var(--t-border-strong)", padding: "0 2px", lineHeight: 1.7 }}>
              Del — remove<br />Esc — cancel<br />Ctrl+Z — undo<br />Ctrl+C/V — copy<br />Ctrl+A — select all<br />Space+drag — pan<br />Ctrl+scroll — zoom
            </div>
          </>
        )}
      </div>

      {/* Canvas viewport */}
      <div ref={wrapRef}
        style={{ flex: 1, overflow: "hidden", position: "relative", cursor, background: "var(--t-page)" }}
        onMouseDown={handleCanvasMouseDown}
        onClick={handleCanvasClick}>

        {/* Transformed canvas content */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", transformOrigin: "0 0", transform: `translate(${xfm.tx}px,${xfm.ty}px) scale(${xfm.scale})` }}>
          {/* Background grid */}
          <div style={{
            position: "absolute", top: -2000, left: -2000, width: 6000, height: 6000,
            backgroundImage: "radial-gradient(circle, #1e2a3a 1px, transparent 1px)",
            backgroundSize: `${GRID_SNAP}px ${GRID_SNAP}px`,
          }} />

          {/* SVG edge layer */}
          <svg style={{ position: "absolute", top: -2000, left: -2000, width: 6000, height: 6000, pointerEvents: "none", overflow: "visible" }}>
            <defs>
              <marker id="arr" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill="#475569" />
              </marker>
              <marker id="arr-sel" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill="#f59e0b" />
              </marker>
            </defs>
            {edges.map(e => {
              const fn = nodes.find(n => n.id === e.from);
              const tn = nodes.find(n => n.id === e.to);
              if (!fn || !tn) return null;
              const { x1, y1, x2, y2 } = getAnchor(fn, tn);
              const ax1 = x1 + 2000, ay1 = y1 + 2000, ax2 = x2 + 2000, ay2 = y2 + 2000;
              const isSel = selEdge === e.id;
              const dash = e.type === "fiber" ? "8,4" : e.type === "wireless" ? "2,6" : e.type === "vpn" ? "6,3" : "none";
              const pd = edgePathD(ax1, ay1, ax2, ay2, e.type === "curved" || e.type === "elbow" ? e.type : "straight");
              const mx = (ax1 + ax2) / 2, my = (ay1 + ay2) / 2;
              return (
                <g key={e.id} style={{ pointerEvents: "stroke" }}>
                  <path d={edgePathD(ax1, ay1, ax2, ay2, e.type === "curved" || e.type === "elbow" ? e.type : "straight")}
                    stroke="transparent" strokeWidth={14} fill="none"
                    style={{ cursor: "pointer", pointerEvents: "stroke" }}
                    onClick={ev => { ev.stopPropagation(); setSelEdge(e.id); setSelNodes(new Set()); }}
                  />
                  <path d={pd} stroke={isSel ? "#f59e0b" : "var(--t-border-strong)"}
                    strokeWidth={isSel ? 2.5 : 1.5} strokeDasharray={dash === "none" ? undefined : dash}
                    fill="none" markerEnd={`url(#${isSel ? "arr-sel" : "arr"})`}
                    style={{ pointerEvents: "none" }}
                  />
                  {e.label && (
                    <text x={mx} y={my - 6} textAnchor="middle" fill={isSel ? "#fbbf24" : "#64748b"} fontSize={11} fontFamily="system-ui" style={{ pointerEvents: "none" }}>{e.label}</text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Rubber band */}
          {rubberBand && (
            <div style={{
              position: "absolute",
              left: Math.min(rubberBand.sx, rubberBand.ex),
              top:  Math.min(rubberBand.sy, rubberBand.ey),
              width:  Math.abs(rubberBand.ex - rubberBand.sx),
              height: Math.abs(rubberBand.ey - rubberBand.sy),
              border: "1.5px dashed #3b82f6",
              background: "rgba(59,130,246,0.06)",
              pointerEvents: "none",
              zIndex: 100,
            }} />
          )}

          {/* Nodes */}
          {nodes.map(n => {
            const cfg   = NODE_TYPES[n.type] || NODE_TYPES.server;
            const isSel = selNodes.has(n.id);
            const isCF  = connectFrom === n.id;
            const nw    = n.w || NODE_W;
            const nh    = n.h || NODE_H;
            const fillC = n.props?.fillColor   || (cfg.shape === "note" ? "#fef3c7" : tint(cfg.color, 9));
            const bordC = n.props?.borderColor || cfg.color;
            const textC = n.props?.textColor   || (cfg.shape === "note" ? "#92400e" : "#e2e8f0");
            const isGrabbing = dragging?.nodeId === n.id;
            let borderRadius = 8;
            if (cfg.shape === "rounded") borderRadius = 20;
            if (cfg.shape === "note") borderRadius = "4px 14px 4px 4px";
            if (cfg.shape === "diamond") borderRadius = "0 8px 0 8px";

            return (
              <div key={n.id}
                onMouseDown={ev => handleNodeMouseDown(ev, n.id)}
                style={{
                  position: "absolute", left: n.x, top: n.y, width: nw, height: nh,
                  background: fillC,
                  border: `2px solid ${isSel ? "#60a5fa" : isCF ? "#fbbf24" : bordC}`,
                  borderRadius,
                  cursor: isGrabbing ? "grabbing" : "grab",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 4, padding: 8, boxSizing: "border-box",
                  boxShadow: isSel
                    ? `0 0 0 2px ${tint(bordC, 33)}, 0 4px 20px rgba(0,0,0,0.5)`
                    : "0 2px 8px rgba(0,0,0,0.4)",
                  transition: isGrabbing ? "none" : "box-shadow 0.1s",
                  userSelect: "none",
                  transform: cfg.shape === "diamond" ? "rotate(45deg)" : "none",
                }}>
                <div style={{
                  background: tint(bordC, 20), color: bordC, borderRadius: 4,
                  padding: "1px 6px", fontSize: 9, fontWeight: 800, fontFamily: "monospace",
                  transform: cfg.shape === "diamond" ? "rotate(-45deg)" : "none",
                }}>{cfg.symbol}</div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: textC,
                  textAlign: "center", lineHeight: 1.3,
                  maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  transform: cfg.shape === "diamond" ? "rotate(-45deg)" : "none",
                }}>
                  {n.label}
                </div>
                {n.props?.ip && (
                  <div style={{ fontSize: 9, color: textC + "aa", fontFamily: "monospace", transform: cfg.shape === "diamond" ? "rotate(-45deg)" : "none" }}>{n.props.ip}</div>
                )}
                {isSel && (
                  <div style={{
                    position: "absolute", inset: -5, border: "2px solid #3b82f6",
                    borderRadius: borderRadius === 8 ? 12 : borderRadius,
                    pointerEvents: "none",
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right properties panel */}
      {(selNode || selEdgeObj) && (
        <div style={{ width: isMobile ? "100%" : 220, flexShrink: 0, maxHeight: isMobile ? 220 : undefined, borderLeft: isMobile ? "none" : "1px solid #1e293b", borderTop: isMobile ? "1px solid #1e293b" : "none", background: "var(--t-panel-hi)", overflow: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 11, zIndex: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {selNodes.size > 1 ? `${selNodes.size} nodes` : selNode ? "Node" : "Connection"}
            </span>
            <button onClick={() => { setSelNodes(new Set()); setSelEdge(null); }} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
          </div>

          {selNode && (
            <>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>Label</label>
                <input type="text" value={selNode.label || ""} onChange={e => updateNodeProp("label", e.target.value)} style={ipt} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>Type</label>
                <select value={selNode.type} onChange={e => updateNodeProp("type", e.target.value)} style={ipt}>
                  {Object.entries(NODE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div style={{ height: 1, background: "var(--t-border-soft)" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Style</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { label: "Fill", field: "fillColor" },
                  { label: "Border", field: "borderColor" },
                  { label: "Text", field: "textColor" },
                ].map(f => (
                  <div key={f.field}>
                    <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600 }}>{f.label}</label>
                    <input type="color" value={selNode.props?.[f.field] || "#3b82f6"}
                      onChange={e => updateNodeIProp(f.field, e.target.value)}
                      style={{ width: "100%", height: 28, borderRadius: 4, border: "1px solid var(--t-border-soft)", cursor: "pointer", background: "none" }} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600 }}>Reset</label>
                  <button onClick={() => {
                    const next = nodes.map(n => selNodes.has(n.id) ? { ...n, props: { ...n.props, fillColor: undefined, borderColor: undefined, textColor: undefined } } : n);
                    setNodes(next); emit(next, edges);
                  }} style={{ width: "100%", height: 28, background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#64748b", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>↺</button>
                </div>
              </div>
              <div style={{ height: 1, background: "var(--t-border-soft)" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Properties</div>
              {[
                { label: "IP Address", field: "ip" },
                { label: "Hostname",   field: "hostname" },
                { label: "OS",         field: "os" },
                { label: "Notes",      field: "notes" },
              ].map(f => (
                <div key={f.field}>
                  <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600 }}>{f.label}</label>
                  <input type="text" value={selNode.props?.[f.field] || ""} onChange={e => updateNodeIProp(f.field, e.target.value)} style={ipt} />
                </div>
              ))}
              <button onClick={() => {
                const nextN = nodes.filter(n => !selNodes.has(n.id));
                const nextE = edges.filter(e => !selNodes.has(e.from) && !selNodes.has(e.to));
                setNodes(nextN); setEdges(nextE); emit(nextN, nextE); setSelNodes(new Set());
              }} style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6, padding: "7px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                Delete {selNodes.size > 1 ? `${selNodes.size} Nodes` : "Node"}
              </button>
            </>
          )}

          {selEdgeObj && (
            <>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>Label</label>
                <input type="text" value={selEdgeObj.label || ""} onChange={e => updateEdge("label", e.target.value)} placeholder="e.g. 1Gbps" style={ipt} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>Style</label>
                <select value={selEdgeObj.type || "straight"} onChange={e => updateEdge("type", e.target.value)} style={ipt}>
                  {["straight","curved","elbow","fiber","vpn","wireless"].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <button onClick={() => {
                const next = edges.filter(e => e.id !== selEdge);
                setEdges(next); emit(nodes, next); setSelEdge(null);
              }} style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 6, padding: "7px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                Delete Connection
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function tbBtn(active, color) {
  return {
    background: active ? (color || "#4f6fff") + (color ? "33" : "") : "transparent",
    border: `1px solid ${active ? (color || "#2563eb") : "var(--t-border-soft)"}`,
    color: active ? (color || "#93c5fd") : "#94a3b8",
    borderRadius: 6, padding: "5px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.1s",
  };
}

// ─── New Map Modal ────────────────────────────────────────────────────────────
function NewMapModal({ clients, onClose, onCreated }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ title: "", description: "", client_id: "", map_type: "network" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const ipt = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 12px", color: "#f1f5f9", fontSize: 13, width: "100%", boxSizing: "border-box" };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setErr("Title is required"); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/infrastructure-maps", { method: "POST", body: JSON.stringify(form) });
      if (!r.ok) { const d = await r.json(); setErr(d.error || "Failed"); return; }
      const map = await r.json();
      onCreated(map);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 16 : 0 }}>
      <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 14, width: isMobile ? "100%" : 440, maxWidth: 440, maxHeight: isMobile ? "85vh" : undefined, overflow: isMobile ? "auto" : undefined, boxShadow: "0 25px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>New Diagram</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {err && <div style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", borderRadius: 7, padding: "8px 12px", fontSize: 13 }}>{err}</div>}
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Diagram Title *</label>
            <input type="text" placeholder="e.g. Client A — Network Topology" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={ipt} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Description</label>
            <textarea rows={2} placeholder="What this diagram covers…" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={ipt} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Client</label>
              <select value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} style={ipt}>
                <option value="">No client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 5, fontWeight: 600 }}>Diagram Type</label>
              <select value={form.map_type} onChange={e => setForm(p => ({ ...p, map_type: e.target.value }))} style={ipt}>
                {MAP_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--t-border-soft)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "none", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 18px", color: "#64748b", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ background: saving ? "#1e3a8a" : "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer" }}>
            {saving ? "Creating…" : "Create Diagram"}
          </button>
        </div>
      </div>
    </div>
  );
}
